/**
 * Krok 6: Výsledky valuace, v12
 * FREE: Hlavní číslo + rozsah + 1 varování
 * ROZŠÍŘENÁ: Finanční zdraví, KPI dashboard, Kvalita byznysu, Doporučení
 * PRO: Rizikové faktory, Simulace růstu, Breakdown valuace
 * AUDIT: Audit Engine, ztracený zisk, moduly, expertní analýza
 */
import { NavrhZVysledku } from './NavrhHodnoty';
import React, { useState, useEffect, useRef } from 'react';
import { runAudit } from '../api/auditApi';
import { generateAIReport } from '../api/aiReportApi';
import { calculateValuation } from '../api/valuationCalculator';
import { WhatIfSimulator } from './WhatIfSimulator';
import { ZpravaPanel } from './ZpravaPanel';
import { ValuaceChat } from './ValuaceChat';
import { getBillingStatus, getEntitlements, startCheckout, updateValuation } from '../lib/apiFetch';
import html2pdf from 'html2pdf.js';

// Které tiery jsou zahrnuté v zaplaceném tieru (komplet = pro + audit)
function tierCovered(paidTiers, target) {
  if (paidTiers.includes('komplet')) return target === 'pro' || target === 'audit' || target === 'komplet';
  return paidTiers.includes(target);
}

// ── Formátování ────────────────────────────────────────────────────────────────
function fmt(v) {
  if (v === null || v === undefined) return '-';
  return Math.round(v).toLocaleString('cs-CZ');
}
function fmtPct(v, decimals = 1) {
  if (v === null || v === undefined) return '-';
  return `${Number(v).toFixed(decimals)} %`;
}
function fmtX(v, decimals = 2) {
  if (v === null || v === undefined) return '-';
  return `${Number(v).toFixed(decimals)}×`;
}

// ── Semafor (tečka) ────────────────────────────────────────────────────────────
function StatusDot({ status }) {
  const colors = { good: 'var(--color-success)', warning: 'var(--gold)', critical: 'var(--color-danger)', unknown: 'var(--text-dim)' };
  return (
    <span style={{
      display: 'inline-block', width: 10, height: 10, borderRadius: '50%',
      background: colors[status] || colors.unknown, marginRight: 4, flexShrink: 0,
    }} />
  );
}

// ── Štítek plánu ──────────────────────────────────────────────────────────────
function PlanBadge({ plan }) {
  const cfg = {
    free:    { label: 'FREE',         color: 'var(--text-dim)', bg: 'var(--purple-deep)', border: 'var(--purple-mid)' },
    pro:     { label: 'PRO VALUACE',  color: 'var(--purple-glow)', bg: 'var(--purple-deep)', border: 'var(--purple-bright)' },
    audit:   { label: 'AUDIT',        color: 'var(--gold)', bg: 'rgba(184,147,45,0.10)', border: 'rgba(184,147,45,0.45)' },
    komplet: { label: 'PRO + AUDIT',  color: 'var(--purple-glow)', bg: 'var(--purple-deep)', border: 'var(--purple-mid)' },
  };
  const c = cfg[plan] || cfg.free;
  return (
    <span style={{
      display: 'inline-block',
      padding: '0.3rem 0.85rem', borderRadius: 999,
      background: c.bg, border: `1.5px solid ${c.border}`,
      color: c.color, fontWeight: 700, fontSize: '0.78rem',
      letterSpacing: '0.03em', marginBottom: '1.5rem',
    }}>
      {c.label}
    </span>
  );
}

// ── Obsah (navigace po sekcích, netiskne se do PDF) ──────────────────────────
function ResultsTOC({ sections }) {
  const go = (id) => { const el = document.getElementById(id); if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' }); };
  return (
    <div data-html2canvas-ignore="true" style={{ background: 'var(--purple-deep)', border: '1px solid var(--purple-mid)', borderRadius: 12, padding: '0.9rem 1.1rem', marginBottom: '1.25rem' }}>
      <div style={{ fontWeight: 700, color: 'var(--star-white)', marginBottom: '0.5rem', fontSize: '0.9rem' }}>Obsah</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
        {sections.map(([id, title]) => (
          <button key={id} onClick={() => go(id)} style={{ background: 'white', border: '1px solid var(--purple-mid)', color: 'var(--purple-glow)', borderRadius: 999, padding: '0.3rem 0.7rem', fontSize: '0.8rem', cursor: 'pointer' }}>
            {title}
          </button>
        ))}
      </div>
    </div>
  );
}

// ── Metodologie (skládací panel) ─────────────────────────────────────────────
function MethodologyBlock({ velikost }) {
  const [open, setOpen] = React.useState(false);
  return (
    <div style={{ marginBottom: '1.25rem', border: '1px solid var(--purple-mid)', borderRadius: 10, background: 'var(--purple-deep)', overflow: 'hidden' }}>
      <button
        onClick={() => setOpen(!open)}
        style={{
          width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '0.7rem 1rem', background: 'none', border: 'none', cursor: 'pointer',
          fontSize: '0.82rem', fontWeight: 600, color: 'var(--text-dim)',
        }}
      >
        <span>Jak počítáme hodnotu firmy?</span>
        <span style={{ fontSize: '0.75rem', transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>▼</span>
      </button>
      {open && (
        <div style={{ padding: '0 1rem 0.85rem', fontSize: '0.8rem', color: 'var(--star-white)', lineHeight: 1.65 }}>
          <p style={{ margin: '0 0 0.5rem' }}>
            Ziskovou firmu oceňujeme <strong>násobkem provozního zisku (EBITDA)</strong>. Výchozím bodem je
            medián tržních násobků pro firmy stejné velikosti v regionu.
          </p>
          <p style={{ margin: '0 0 0.5rem' }}>
            Kvalita podnikání (marže, růst, přenositelnost, koncentrace a další oblasti podle typu firmy)
            násobek posune nejvýš o čtvrtinu nahoru nebo dolů. Každá oblast se počítá jednou.
          </p>
          <p style={{ margin: '0 0 0.5rem' }}>
            Hodnota nesmí klesnout pod majetek: u firmy s nemovitostí pod čistý majetek s nemovitostí
            v tržní hodnotě, jinak pod likvidační hodnotu. Ztrátovou firmu oceňujeme podle toho, co ji drží.
          </p>
          {velikost && velikost.nasobek_median && (
            <p style={{ margin: 0 }}>
              <strong>Velikost firmy:</strong>{' '}
              {velikost.druh === 'trh'
                ? `u firmy s EBITDA přibližně ${Number(velikost.ebitda_eur).toLocaleString('cs-CZ')} EUR je medián tržního násobku ${Number(velikost.nasobek_median).toFixed(1).replace('.', ',')}×. Zdroj: ${velikost.zdroj}.`
                : `pro firmy s EBITDA pod 200 tis. EUR tržní data násobek neuvádějí. Vycházíme z násobku na této hranici, sníženého podle velikosti na ${Number(velikost.nasobek_median).toFixed(1).replace('.', ',')}× (odborný odhad).`}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// ── FREE sekce ─────────────────────────────────────────────────────────────────
function MetricBadge({ status }) {
  const cfg = {
    good:    { bg: 'rgba(47,143,91,0.14)', color: 'var(--color-success)', label: 'Dobré' },
    warning: { bg: 'rgba(184,147,45,0.12)', color: 'var(--gold)', label: 'Průměrné' },
    bad:     { bg: 'rgba(192,69,63,0.18)', color: 'var(--color-danger)', label: 'Slabé' },
  };
  const c = cfg[status] || cfg.warning;
  return (
    <span style={{ display: 'inline-block', padding: '0.15rem 0.55rem', borderRadius: 999, background: c.bg, color: c.color, fontSize: '0.72rem', fontWeight: 700 }}>
      {c.label}
    </span>
  );
}

function FreeMetricCard({ label, value, status }) {
  return (
    <div style={{ padding: '0.85rem 1rem', border: '1px solid var(--purple-mid)', borderRadius: 10, background: 'var(--purple-dark)', display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
      <div style={{ fontSize: '0.75rem', color: 'var(--text-dim)' }}>{label}</div>
      <div style={{ fontWeight: 800, fontSize: '1.1rem', color: 'var(--star-white)' }}>{value}</div>
      <MetricBadge status={status} />
    </div>
  );
}

function LockedSection({ title, description }) {
  return (
    <div style={{
      padding: '0.75rem 1rem',
      border: '1px solid var(--purple-mid)',
      borderRadius: 8,
      background: 'var(--purple-deep)',
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem',
      opacity: 0.85,
    }}>
      <div>
        <span style={{ fontWeight: 700, fontSize: '0.85rem', color: 'var(--star-white)' }}>{title}</span>
        {description && <span style={{ fontSize: '0.78rem', color: 'var(--text-dim)', marginLeft: '0.4rem' }}>- {description}</span>}
      </div>
    </div>
  );
}

// ── Záporná EBITDA, speciální banner ──────────────────────────────────────
function NegativeEbitdaBanner({ ebitda, metrics, bq, onSwitchToAudit, busy, priceLabel = '', alreadyHasAudit = false }) {
  const bqTotal = Math.round((bq?.total || 0) * 100);
  const bqStatus = bqTotal >= 70 ? 'good' : bqTotal >= 50 ? 'warning' : 'bad';
  const grossMarginStatus = metrics.gross_margin_pct >= 40 ? 'good' : metrics.gross_margin_pct >= 25 ? 'warning' : 'bad';
  const yoyStatus = metrics.yoy_growth_pct >= 20 ? 'good' : metrics.yoy_growth_pct >= 0 ? 'warning' : 'bad';

  return (
    <div style={{ marginBottom: '2rem' }}>
      {/* Hlavní banner, záporná EBITDA */}
      <div style={{ textAlign: 'center', padding: '2rem 1.5rem', background: 'linear-gradient(135deg, var(--gold) 0%, var(--gold) 100%)', borderRadius: 16, marginBottom: '1.25rem', color: 'white' }}>
        <div style={{ fontSize: '0.85rem', opacity: 0.85, marginBottom: '0.35rem', letterSpacing: '0.05em', textTransform: 'uppercase' }}>EBITDA je záporná</div>
        <div style={{ fontFamily: "'Source Serif 4', Georgia, serif", fontSize: '2.2rem', fontWeight: 400, letterSpacing: '-0.02em', marginBottom: '0.5rem' }}>
          {fmt(ebitda)} Kč
        </div>
        <div style={{ fontSize: '0.9rem', opacity: 0.9, maxWidth: 520, margin: '0 auto' }}>
          Firma se proto neoceňuje násobkem zisku. Metodu jsme vybrali podle majetku a vývoje
          tržeb, ocenění najdete níže.
        </div>
      </div>

      {/* Vysvětlení + CTA audit, jen tam, kde audit dává smysl */}
      {onSwitchToAudit && <div style={{ padding: '1.25rem', background: 'rgba(184,147,45,0.10)', border: '1px solid rgba(184,147,45,0.35)', borderRadius: 12, marginBottom: '1.25rem' }}>
        <div style={{ fontWeight: 700, fontSize: '1rem', color: 'var(--gold)', marginBottom: '0.5rem' }}>
          Proč to neznamená, že firma nemá hodnotu?
        </div>
        <div style={{ fontSize: '0.88rem', color: 'var(--gold)', lineHeight: 1.6, marginBottom: '0.75rem' }}>
          Násobek zisku tu použít nelze, hodnotu proto určuje jiná metoda (viz ocenění níže).
          Mnoho firem má zápornou EBITDA v růstové fázi nebo kvůli jednorázovým investicím.
          K ocenění proto nabízíme i <strong>konzultační audit firmy</strong>:
          placená hloubková analýza, která ukáže, kde přesně ztrácíte peníze a <strong>co konkrétně zlepšit</strong>,
          aby se firma dostala do zisku, krok za krokem.
        </div>
        <button
          onClick={busy || alreadyHasAudit ? undefined : onSwitchToAudit}
          disabled={busy || alreadyHasAudit}
          style={{
            padding: '0.7rem 1.5rem', background: (busy || alreadyHasAudit) ? 'var(--text-dim)' : 'var(--purple-glow)', color: 'white',
            border: 'none', borderRadius: 8, fontWeight: 700, fontSize: '0.92rem',
            cursor: (busy || alreadyHasAudit) ? 'default' : 'pointer',
          }}
        >
          {alreadyHasAudit ? 'Audit je odemčený níže ↓'
            : busy ? 'Přesměrování na audit…'
            : `Objednat konzultační audit firmy${priceLabel}`}
        </button>
        {!alreadyHasAudit && (
          <div style={{ fontSize: '0.78rem', color: 'var(--gold)', marginTop: '0.55rem' }}>
            Placená služba. Po objednání připravíme kompletní audit s konkrétními kroky ke zvýšení zisku.
          </div>
        )}
      </div>}

      {/* Metriky, ty fungují i při záporné EBITDA */}
      <h3 style={{ fontSize: '0.9rem', color: 'var(--text-dim)', fontWeight: 600, marginBottom: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Přehled klíčových metrik</h3>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '0.65rem', marginBottom: '1.5rem' }}>
        <FreeMetricCard label="EBITDA" value={`${fmt(ebitda)} Kč`} status="bad" />
        <FreeMetricCard label="EBITDA marže" value={fmtPct(metrics.ebitda_margin_pct)} status="bad" />
        <FreeMetricCard label="Hrubá marže" value={fmtPct(metrics.gross_margin_pct)} status={grossMarginStatus} />
        <FreeMetricCard label="Meziroční růst" value={fmtPct(metrics.yoy_growth_pct)} status={yoyStatus} />
        <FreeMetricCard label="Business Quality Score" value={`${bqTotal} / 100`} status={bqStatus} />
      </div>
    </div>
  );
}

function FreeSection({ v, metrics, bq, scenarios, problems, plan, onUpgrade, auditDostupny = true, navrh = null }) {
  // Thresholds pro barevné hodnocení
  const ebitdaStatus = v.ebitda > 0 ? (metrics.ebitda_margin_pct >= 15 ? 'good' : 'warning') : 'bad';
  const ebitdaMarginStatus = metrics.ebitda_margin_pct >= 15 ? 'good' : metrics.ebitda_margin_pct >= 8 ? 'warning' : 'bad';
  const grossMarginStatus  = metrics.gross_margin_pct  >= 40 ? 'good' : metrics.gross_margin_pct  >= 25 ? 'warning' : 'bad';
  const yoyStatus          = metrics.yoy_growth_pct    >= 20 ? 'good' : metrics.yoy_growth_pct    >= 0  ? 'warning' : 'bad';
  const rrsStatus          = v.rrs_pct >= 45 ? 'good' : v.rrs_pct >= 25 ? 'warning' : 'bad';
  const bqTotal            = Math.round((bq?.total || 0) * 100);
  const bqStatus           = bqTotal >= 70 ? 'good' : bqTotal >= 50 ? 'warning' : 'bad';

  // Teaser, potenciál z nejvyššího growth scénáře
  const bestScenario = scenarios && scenarios.length > 0
    ? scenarios.reduce((a, b) => (b.new_valuation > a.new_valuation ? b : a))
    : null;
  const potencial = bestScenario ? bestScenario.new_valuation - v.total : null;

  // ── 3 klíčové takeaways ──
  // 1) Nejslabší BQ dimenze
  const dimLabels = {
    customer_quality: 'Kvalita zákazníků', margin_quality: 'Kvalita marží',
    acquisition_quality: 'Akvizice', growth_quality: 'Růst',
    business_model: 'Business model', transferability: 'Přenositelnost',
    inventory_management: 'Zásoby', concentration_risk: 'Koncentrační riziko',
  };
  let weakestDim = null;
  if (bq?.dimensions) {
    let minVal = Infinity;
    for (const [key, val] of Object.entries(bq.dimensions)) {
      // null = dimenze se u typu podnikání nehodnotí
      if (val != null && val < minVal) { minVal = val; weakestDim = { key, val, label: dimLabels[key] || key }; }
    }
  }
  // Můstek se ukáže, jen když o dluhu něco víme. Dosazená nula by tvrdila,
  // že firma dluh nemá, a to není totéž jako že ho neznáme.
  const maMustek = v.equity != null && v.net_debt != null;

  // Hlavní číslo bere obrazovka z návrhu hodnoty, stejně jako zpráva, AI
  // výklad i chat (plán v2, O1). Jádro dává jen násobek. Návrh je v tis. Kč.
  const navrhHodnota = navrh ? (navrh.hodnota_pro_majitele ?? navrh.hodnota_provozu) : null;
  const zNavrhu = navrhHodnota != null;
  const metodaVynos = !zNavrhu || ['ebitda', 'sde'].includes(navrh.metoda?.klic);
  const proMajitele = zNavrhu ? navrh.hodnota_pro_majitele != null : maMustek;
  const hlavni = zNavrhu ? navrhHodnota * 1000 : (maMustek ? v.equity : v.total);
  const pasmoOd = zNavrhu ? (navrh.rozpeti ? navrh.rozpeti.od * 1000 : null) : (maMustek ? v.equity_conservative : v.conservative);
  const pasmoDo = zNavrhu ? (navrh.rozpeti ? navrh.rozpeti.do * 1000 : null) : (maMustek ? v.equity_optimistic : v.optimistic);
  const procento = (x) => (hlavni ? Math.round(Math.abs(x - hlavni) / Math.abs(hlavni) * 100) : 0);

  // 2) Největší riziko
  const biggestRisk = problems && problems.length > 0
    ? problems.reduce((a, b) => Math.abs(b.valuation_impact_pct || 0) > Math.abs(a.valuation_impact_pct || 0) ? b : a)
    : null;

  return (
    <div style={{ marginBottom: '2rem' }}>
      {/* Hlavní číslo. Když známe dluh, je jím hodnota pro majitele:
          úvěry přecházejí na kupujícího nebo se splácejí z kupní ceny,
          takže hodnota provozu není to, co majitel dostane. */}
      <div style={{ textAlign: 'center', padding: '2rem 1.5rem', background: 'linear-gradient(135deg, var(--star-white) 0%, var(--purple-glow) 100%)', borderRadius: 16, marginBottom: '1rem', color: 'white' }}>
        <div style={{ fontSize: '0.85rem', opacity: 0.8, marginBottom: '0.35rem', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
          {proMajitele ? 'Hodnota pro majitele' : 'Odhadovaná hodnota provozu'}
        </div>
        <div style={{ fontFamily: "'Source Serif 4', Georgia, serif", fontSize: '2.8rem', fontWeight: 400, letterSpacing: '-0.02em', marginBottom: '0.5rem' }}>
          {fmt(hlavni)} Kč
        </div>
        {pasmoOd != null && pasmoDo != null ? (
          <div style={{ fontSize: '0.9rem', opacity: 0.85 }}>
            Rozsah: <strong>{fmt(pasmoOd)} Kč</strong> až <strong>{fmt(pasmoDo)} Kč</strong>
          </div>
        ) : (
          <div style={{ fontSize: '0.9rem', opacity: 0.85 }}>
            {navrh?.metoda?.nazev}: hodnotu určuje majetek, rozpětí z modelu zisku se nepoužije.
          </div>
        )}
      </div>

      {!metodaVynos ? null : maMustek ? (
        <div style={{ border: '1px solid var(--purple-mid)', borderRadius: 12, padding: '1rem 1.2rem', marginBottom: '1rem', background: 'var(--purple-dark)' }}>
          <div style={{ fontSize: '0.68rem', letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--text-dim)', fontWeight: 600, marginBottom: '0.6rem' }}>
            Od hodnoty provozu k hodnotě pro majitele
          </div>
          <RadekMustku popis="Hodnota provozu" castka={v.total} />
          {v.interest_bearing_debt ? (
            <RadekMustku popis="Úvěry a půjčky" castka={-v.interest_bearing_debt}
                         pozn="přecházejí na kupujícího nebo se splácejí z kupní ceny" />
          ) : null}
          {v.cash ? (
            <RadekMustku popis="Peněžní prostředky" castka={v.cash}
                         pozn={v.cash > 0 ? 'zůstávají ve firmě a hodnotu zvyšují' : 'čerpaný kontokorent hodnotu snižuje'} />
          ) : null}
          <RadekMustku popis="Hodnota pro majitele" castka={v.equity} souhrn />
        </div>
      ) : (
        <div style={{ border: '1px solid var(--purple-mid)', borderRadius: 12, padding: '0.85rem 1.1rem', marginBottom: '1rem', fontSize: '0.85rem', color: 'var(--text-dim)' }}>
          Uvedená částka je hodnota provozu. Kolik z ní zbude majiteli, závisí
          na úvěrech a hotovosti, které nemáme vyplněné. Firma s úvěrem má pro
          majitele cenu nižší.
        </div>
      )}

      {/* Tři boxy. Musí ukazovat totéž pásmo jako hlavička, jinak by
          jedno tvrdilo 45,9 až 60,2 a druhé 47,0 až 61,3. */}
      {pasmoOd != null && pasmoDo != null && (
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem', marginBottom: '1.5rem' }}>
        <div style={{ padding: '1rem', border: '1px solid var(--purple-mid)', borderRadius: 10, background: 'var(--purple-deep)', textAlign: 'center' }}>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-dim)', marginBottom: '0.25rem' }}>Konzervativní (−{procento(pasmoOd)} %)</div>
          <div style={{ fontWeight: 700, fontSize: '1rem', color: 'var(--star-white)' }}>{fmt(pasmoOd)} Kč</div>
        </div>
        <div style={{ padding: '1rem', border: '2px solid var(--purple-glow)', borderRadius: 10, background: 'var(--purple-deep)', textAlign: 'center' }}>
          <div style={{ fontSize: '0.75rem', color: 'var(--purple-glow)', marginBottom: '0.25rem' }}>Střední odhad</div>
          <div style={{ fontWeight: 800, fontSize: '1.15rem', color: 'var(--purple-glow)' }}>{fmt(hlavni)} Kč</div>
        </div>
        <div style={{ padding: '1rem', border: '1px solid var(--purple-mid)', borderRadius: 10, background: 'var(--purple-deep)', textAlign: 'center' }}>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-dim)', marginBottom: '0.25rem' }}>Optimistický (+{procento(pasmoDo)} %)</div>
          <div style={{ fontWeight: 700, fontSize: '1rem', color: 'var(--star-white)' }}>{fmt(pasmoDo)} Kč</div>
        </div>
      </div>
      )}

      {/* Cash Flow perspektiva, jen u ocenění výnosem */}
      {v.ebitda > 0 && metodaVynos && (
        <div style={{ padding: '1rem', background: 'rgba(47,143,91,0.08)', border: '1px solid rgba(47,143,91,0.35)', borderRadius: 10, marginBottom: '1.25rem' }}>
          <div style={{ fontSize: '0.82rem', color: 'var(--color-success)', lineHeight: 1.6 }}>
            Vaše firma generuje <strong>{fmt(v.ebitda)} Kč</strong> ročního provozního zisku.
            Kupující platící <strong>{fmt(v.total)} Kč</strong> očekává návratnost investice
            za <strong>{v.total > 0 && v.ebitda > 0 ? (v.total / v.ebitda).toFixed(1) : '-'} let</strong>.
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem', marginTop: '0.75rem' }}>
            <div style={{ textAlign: 'center', padding: '0.6rem', background: 'white', borderRadius: 8, border: '1px solid rgba(47,143,91,0.14)' }}>
              <div style={{ fontSize: '0.7rem', color: 'var(--text-dim)' }}>Roční cash flow</div>
              <div style={{ fontWeight: 700, fontSize: '0.95rem', color: 'var(--color-success)' }}>{fmt(v.ebitda)} Kč</div>
            </div>
            <div style={{ textAlign: 'center', padding: '0.6rem', background: 'white', borderRadius: 8, border: '1px solid rgba(47,143,91,0.14)' }}>
              <div style={{ fontSize: '0.7rem', color: 'var(--text-dim)' }}>Návratnost</div>
              <div style={{ fontWeight: 700, fontSize: '0.95rem', color: 'var(--color-success)' }}>
                {v.total > 0 && v.ebitda > 0 ? (v.total / v.ebitda).toFixed(1) : '-'} let
              </div>
            </div>
            <div style={{ textAlign: 'center', padding: '0.6rem', background: 'white', borderRadius: 8, border: '1px solid rgba(47,143,91,0.14)' }}>
              <div style={{ fontSize: '0.7rem', color: 'var(--text-dim)' }}>Roční výnosnost</div>
              <div style={{ fontWeight: 700, fontSize: '0.95rem', color: 'var(--color-success)' }}>
                {v.total > 0 && v.ebitda > 0 ? ((v.ebitda / v.total) * 100).toFixed(1) : '-'} %
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 3 klíčové takeaways */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '0.5rem', marginBottom: '1.25rem' }}>
        {/* 1. Co nejvíc táhne valuaci dolů */}
        {weakestDim && (
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.6rem', padding: '0.65rem 0.85rem', background: 'rgba(192,69,63,0.08)', border: '1px solid rgba(192,69,63,0.35)', borderRadius: 8 }}>
            <div style={{ fontSize: '0.8rem', color: 'var(--color-danger)', lineHeight: 1.5 }}>
              <strong>Nejslabší stránka:</strong> {weakestDim.label} ({Math.round(weakestDim.val * 100)} / 100)
              {plan === 'free' && <span style={{ color: 'var(--text-dim)' }}>, detail v PRO variantě</span>}
            </div>
          </div>
        )}
        {/* 2. Největší příležitost */}
        {bestScenario && potencial > 0 && (
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.6rem', padding: '0.65rem 0.85rem', background: 'rgba(47,143,91,0.08)', border: '1px solid rgba(47,143,91,0.35)', borderRadius: 8 }}>
            <div style={{ fontSize: '0.8rem', color: 'var(--color-success)', lineHeight: 1.5 }}>
              <strong>Největší příležitost:</strong> {bestScenario.label_cs || bestScenario.label || 'Optimalizace'} = +{fmt(potencial)} Kč k valuaci
            </div>
          </div>
        )}
        {/* 3. Největší riziko */}
        {biggestRisk && (
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.6rem', padding: '0.65rem 0.85rem', background: 'rgba(184,147,45,0.10)', border: '1px solid rgba(184,147,45,0.35)', borderRadius: 8 }}>
            <div style={{ fontSize: '0.8rem', color: 'var(--gold)', lineHeight: 1.5 }}>
              <strong>Hlavní riziko:</strong> {biggestRisk.name_cs || biggestRisk.name} ({biggestRisk.severity === 'critical' ? 'kritické' : 'střední'})
              {plan === 'free' && <span style={{ color: 'var(--text-dim)' }}>, jak řešit v PRO variantě</span>}
            </div>
          </div>
        )}
      </div>

      {/* Metodologie */}
      <MethodologyBlock velikost={v.velikost} />

      {/* Metric grid */}
      <h3 style={{ fontSize: '0.9rem', color: 'var(--text-dim)', fontWeight: 600, marginBottom: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Přehled klíčových metrik</h3>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '0.65rem', marginBottom: '1.5rem' }}>
        <FreeMetricCard label="EBITDA" value={`${fmt(v.ebitda)} Kč`} status={ebitdaStatus} />
        <FreeMetricCard label="EBITDA marže" value={fmtPct(metrics.ebitda_margin_pct)} status={ebitdaMarginStatus} />
        <FreeMetricCard label="Hrubá marže" value={fmtPct(metrics.gross_margin_pct)} status={grossMarginStatus} />
        <FreeMetricCard label="Meziroční růst" value={fmtPct(metrics.yoy_growth_pct)} status={yoyStatus} />
        <FreeMetricCard label="Opakující se zákazníci" value={fmtPct(v.rrs_pct, 0)} status={rrsStatus} />
        <FreeMetricCard label="Business Quality Score" value={`${bqTotal} / 100`} status={bqStatus} />
      </div>

      {/* Teaser, potenciál (jen ve FREE) */}
      {plan !== 'pro' && potencial !== null && potencial > 0 && (
        <div style={{ padding: '1rem 1.25rem', background: 'rgba(47,143,91,0.08)', border: '1px solid rgba(47,143,91,0.45)', borderRadius: 10, marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: '0.92rem', color: 'var(--color-success)' }}>
              Na základě analýzy existuje potenciál zvýšit hodnotu firmy o +{fmt(potencial)} Kč
            </div>
            <div style={{ fontSize: '0.78rem', color: 'var(--text-dim)', marginTop: '0.15rem' }}>
              Konkrétní kroky jsou dostupné v PRO variantě
            </div>
          </div>
        </div>
      )}

      {/* Teasery PRO + AUDIT, jen ve FREE */}
      {plan === 'free' && (
        <>
          {/* Teaser uzamčených prémiových skóre */}
          <div style={{ border: '1px dashed var(--purple-mid)', borderRadius: 14, padding: '1rem 1.25rem', background: 'var(--purple-deep)', marginBottom: '0.75rem' }}>
            <div style={{ fontWeight: 700, color: 'var(--star-white)', marginBottom: '0.7rem' }}>
              Jak vaši firmu vidí investor? Odemkněte v PRO:
            </div>
            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
              {[['Investment Grade', 'A-'], ['Exit Readiness', '72/100']].map(([t, v]) => (
                <div key={t} style={{ flex: 1, minWidth: 140, textAlign: 'center', padding: '0.6rem', borderRadius: 10, background: 'white', border: '1px solid var(--purple-mid)' }}>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-dim)' }}>{t}</div>
                  <div style={{ fontFamily: "'Source Serif 4', Georgia, serif", fontSize: '1.6rem', fontWeight: 400, color: 'var(--purple-glow)', filter: 'blur(7px)', userSelect: 'none', lineHeight: 1.2 }}>{v}</div>
                  <div style={{ fontSize: '0.72rem', color: 'var(--purple-glow)', fontWeight: 600 }}>v PRO</div>
                </div>
              ))}
            </div>
          </div>

          {/* PRO teaser */}
          <div style={{ border: '2px solid var(--purple-bright)', borderRadius: 14, padding: '1.25rem', background: 'var(--purple-deep)', marginBottom: '0.75rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.65rem', flexWrap: 'wrap', gap: '0.5rem' }}>
              <div style={{ fontWeight: 800, fontSize: '1rem', color: 'var(--purple-glow)' }}>PRO Valuace</div>
              <span style={{ fontWeight: 800, fontSize: '1rem', color: 'var(--purple-glow)' }}>6 990 Kč</span>
            </div>
            <div style={{ fontSize: '0.85rem', color: 'var(--star-white)', lineHeight: 1.55, marginBottom: '0.5rem' }}>
              Kompletní profesionální valuace s expertním reportem, vše, co potřebujete pro jednání s investorem nebo přípravu na exit.
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
              <LockedSection title="Finanční zdraví a Business Quality Score" description="ROA, ROE, ROS + 8 dimenzí kvality byznysu" />
              <LockedSection title="Jak zvýšit valuaci vaší firmy" description="Konkrétní faktory a jejich dopad na hodnotu v Kč" />
              <LockedSection title="Pohled investora + Exit Readiness Score" description="Investiční hodnocení, silné stránky a rizika" />
              <LockedSection title="Expertní výkladový report" description="Personalizovaná analýza s doporučeními" />
            </div>
            {onUpgrade && (
              <button onClick={() => onUpgrade('pro')} className="btn-primary" style={{ width: '100%', marginTop: '0.75rem', padding: '0.65rem' }}>
                Odemknout PRO valuaci
              </button>
            )}
          </div>

          {/* AUDIT teaser */}
          {auditDostupny && <>
          <div style={{ border: '2px solid rgba(184,147,45,0.45)', borderRadius: 14, padding: '1.25rem', background: 'rgba(184,147,45,0.10)', marginBottom: '0.75rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.65rem', flexWrap: 'wrap', gap: '0.5rem' }}>
              <div style={{ fontWeight: 800, fontSize: '1rem', color: 'var(--gold)' }}>Konzultantský audit</div>
              <span style={{ fontWeight: 800, fontSize: '1rem', color: 'var(--gold)' }}>4 990 Kč</span>
            </div>
            <div style={{ fontSize: '0.85rem', color: 'var(--star-white)', lineHeight: 1.55, marginBottom: '0.5rem' }}>
              Kde ztrácíte zisk a jak to napravit, konkrétní kroky s měřitelným dopadem na vaše podnikání.
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
              <LockedSection title="Analýza ztraceného zisku" description="Kolik peněz ročně necháváte na stole" />
              <LockedSection title="6 audit modulů" description="Marže, retence, reklama, sklady, akvizice, konverze" />
              <LockedSection title="Konzultantský report + SWOT" description="Executive summary, silné/slabé stránky, příležitosti" />
              <LockedSection title="Detailní průvodce akcemi" description="Krok za krokem, jak každou akci provést" />
            </div>
            {onUpgrade && (
              <button onClick={() => onUpgrade('audit')} style={{
                width: '100%', marginTop: '0.75rem', padding: '0.65rem', borderRadius: 8,
                border: '2px solid var(--gold)', background: 'white',
                color: 'var(--gold)', fontWeight: 700, fontSize: '0.88rem', cursor: 'pointer',
              }}>
                Odemknout audit
              </button>
            )}
          </div>

          {/* Bundle CTA, doporučená volba */}
          <div style={{ position: 'relative', border: '2px solid var(--purple-glow)', borderRadius: 14, padding: '1.4rem 1.25rem 1.25rem', background: 'linear-gradient(135deg, var(--purple-deep), var(--purple-deep))', textAlign: 'center' }}>
            <div style={{ position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)', background: 'var(--purple-glow)', color: 'white', fontSize: '0.72rem', fontWeight: 700, padding: '0.2rem 0.8rem', borderRadius: 999, whiteSpace: 'nowrap' }}>
              Nejlepší volba
            </div>
            <div style={{ fontWeight: 800, fontSize: '1.05rem', color: 'var(--star-white)', marginBottom: '0.3rem' }}>PRO + Audit = kompletní řešení</div>
            <div style={{ fontSize: '0.88rem', color: 'var(--text-dim)', marginBottom: '0.75rem' }}>
              <span style={{ textDecoration: 'line-through', marginRight: '0.4rem' }}>11 980 Kč</span>
              <span style={{ fontWeight: 800, fontSize: '1.15rem', color: 'var(--purple-glow)' }}>9 990 Kč</span>
              <span style={{ fontSize: '0.78rem', color: 'var(--color-success)', marginLeft: '0.4rem', fontWeight: 600 }}>Ušetříte 1 990 Kč</span>
            </div>
            {onUpgrade && (
              <button onClick={() => onUpgrade('komplet')} style={{
                width: '100%', padding: '0.65rem', borderRadius: 8,
                border: 'none', background: 'var(--btn-bg)',
                color: 'white', fontWeight: 700, fontSize: '0.88rem', cursor: 'pointer',
              }}>
                Odemknout kompletní analýzu
              </button>
            )}
          </div>
          </>}
        </>
      )}
    </div>
  );
}

// ── Finanční zdraví ────────────────────────────────────────────────────────────
const STATUS_CS = { GOOD: 'Dobré', WARNING: 'Pozor', CRITICAL: 'Kritické', UNKNOWN: 'Nevyhodnoceno' };
const STATUS_BG = { GOOD: 'rgba(47,143,91,0.08)', WARNING: 'rgba(184,147,45,0.10)', CRITICAL: 'rgba(192,69,63,0.08)', UNKNOWN: 'var(--purple-deep)' };
const STATUS_COLOR = { GOOD: 'var(--color-success)', WARNING: 'var(--gold)', CRITICAL: 'var(--color-danger)', UNKNOWN: 'var(--text-dim)' };
const M_STATUS_BG = { good: 'rgba(47,143,91,0.08)', warning: 'rgba(184,147,45,0.10)', critical: 'rgba(192,69,63,0.08)' };
const M_STATUS_BORDER = { good: 'rgba(47,143,91,0.45)', warning: 'rgba(184,147,45,0.45)', critical: 'rgba(192,69,63,0.45)' };

function HealthCards({ fh }) {
  if (!fh || fh.status === 'UNKNOWN') {
    return (
      <div style={{ background: 'var(--purple-deep)', border: '1px solid var(--purple-mid)', borderRadius: 10, padding: '1rem 1.25rem', marginBottom: '1.5rem', color: 'var(--text-dim)', fontSize: '0.88rem' }}>
        Finanční zdraví: rozvahové údaje nebyly zadány, doplňte je v Kroku 1 pro podrobné hodnocení.
      </div>
    );
  }
  return (
    <div style={{ marginBottom: '1.75rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.75rem' }}>
        <h3 style={{ fontFamily: "'Source Serif 4', Georgia, serif", fontWeight: 600, fontSize: '1.05rem', color: 'var(--star-white)', margin: 0 }}>Finanční zdraví</h3>
        <span style={{ padding: '0.2rem 0.65rem', borderRadius: 999, background: STATUS_BG[fh.status], color: STATUS_COLOR[fh.status], fontWeight: 700, fontSize: '0.78rem' }}>
          {STATUS_CS[fh.status] || fh.status}
        </span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '0.6rem' }}>
        {fh.metrics.map(m => (
          <div key={m.key} style={{
            background: M_STATUS_BG[m.status] || 'var(--purple-deep)',
            border: `1px solid ${M_STATUS_BORDER[m.status] || 'var(--purple-mid)'}`,
            borderRadius: 8, padding: '0.75rem',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.15rem' }}>
              <span style={{ fontSize: '0.78rem', color: 'var(--text-dim)' }}>{m.name_cs}</span>
              <StatusDot status={m.status} />
            </div>
            <div style={{ fontWeight: 700, fontSize: '1.15rem', color: 'var(--star-white)' }}>
              {m.key.includes('ratio') || m.key.includes('turnover') ? fmtX(m.value) : fmtPct(m.value)}
            </div>
            <div style={{ fontSize: '0.73rem', color: 'var(--text-dim)' }}>Benchmark: {m.benchmark}</div>
            {m.action_cs && <div style={{ fontSize: '0.73rem', color: 'var(--gold)', marginTop: '0.25rem' }}>{m.action_cs}</div>}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── KPI Metriky ────────────────────────────────────────────────────────────────
function KPIDashboard({ metrics, valuation, segment }) {
  const bm = segment || {};
  const rows = [
    { label: 'EBITDA marže', value: fmtPct(metrics.ebitda_margin_pct), benchmark: bm.benchmark_ebitda_margin ? fmtPct(bm.benchmark_ebitda_margin) : null, rating: metrics.ebitda_margin_pct >= 20 ? 'Výborná' : metrics.ebitda_margin_pct >= 10 ? 'OK' : 'Nízká' },
    { label: 'Hrubá marže', value: fmtPct(metrics.gross_margin_pct), benchmark: bm.benchmark_gross_margin ? fmtPct(bm.benchmark_gross_margin) : null, rating: metrics.gross_margin_pct >= 40 ? 'Výborná' : metrics.gross_margin_pct >= 25 ? 'Průměrná' : 'Nízká' },
    { label: 'Meziroční růst tržeb', value: fmtPct(metrics.yoy_growth_pct), benchmark: null, rating: metrics.yoy_growth_pct >= 30 ? 'Silný' : metrics.yoy_growth_pct >= 10 ? 'Zdravý' : metrics.yoy_growth_pct >= 0 ? 'Pomalý' : 'Pokles' },
    { label: 'RRS (vracející se zákazníci)', value: valuation.rrs_pct != null ? fmtPct(valuation.rrs_pct) : '-', benchmark: bm.benchmark_rrs ? fmtPct(bm.benchmark_rrs) : null, rating: !valuation.rrs_pct ? 'Nezadáno' : valuation.rrs_pct >= 50 ? 'Výborný' : valuation.rrs_pct >= 35 ? 'Dobrý' : valuation.rrs_pct >= 20 ? 'Průměrný' : 'Kritický' },
    { label: 'Obrátkovost zásob', value: metrics.inventory_turnover ? fmtX(metrics.inventory_turnover) : '-', benchmark: null, rating: !metrics.inventory_turnover ? 'Bez zásob' : metrics.inventory_turnover >= 6 ? 'Výborná' : metrics.inventory_turnover >= 3 ? 'OK' : 'Nízká' },
  ];
  const hasBenchmarks = rows.some(r => r.benchmark);
  return (
    <div style={{ marginBottom: '1.75rem' }}>
      <h3 style={{ fontFamily: "'Source Serif 4', Georgia, serif", fontWeight: 600, fontSize: '1.05rem', color: 'var(--star-white)', marginBottom: '0.75rem' }}>
        Klíčové metriky
        {bm.name_cs && <span style={{ fontSize: '0.78rem', fontWeight: 400, color: 'var(--text-dim)' }}> vs. orientační hodnota oboru ({bm.name_cs})</span>}
      </h3>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem' }}>
        {hasBenchmarks && (
          <thead>
            <tr style={{ borderBottom: '2px solid var(--purple-mid)' }}>
              <th style={{ padding: '0.4rem 0.75rem', textAlign: 'left', color: 'var(--text-dim)', fontWeight: 600, fontSize: '0.75rem' }}>Metrika</th>
              <th style={{ padding: '0.4rem 0.75rem', textAlign: 'left', color: 'var(--text-dim)', fontWeight: 600, fontSize: '0.75rem' }}>Vy</th>
              <th style={{ padding: '0.4rem 0.75rem', textAlign: 'left', color: 'var(--text-dim)', fontWeight: 600, fontSize: '0.75rem' }}>Obor</th>
              <th style={{ padding: '0.4rem 0.75rem', textAlign: 'right', color: 'var(--text-dim)', fontWeight: 600, fontSize: '0.75rem' }}>Hodnocení</th>
            </tr>
          </thead>
        )}
        <tbody>
          {rows.map(r => (
            <tr key={r.label} style={{ borderBottom: '1px solid var(--purple-deep)' }}>
              <td style={{ padding: '0.55rem 0.75rem', color: 'var(--star-white)' }}>{r.label}</td>
              <td style={{ padding: '0.55rem 0.75rem', fontWeight: 700, color: 'var(--star-white)' }}>{r.value}</td>
              {hasBenchmarks && <td style={{ padding: '0.55rem 0.75rem', color: 'var(--text-dim)', fontSize: '0.82rem' }}>{r.benchmark || '-'}</td>}
              <td style={{ padding: '0.55rem 0.75rem', color: 'var(--text-dim)', textAlign: 'right' }}>{r.rating}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {hasBenchmarks && (
        <p style={{ fontSize: '0.75rem', color: 'var(--text-dim)', marginTop: '0.5rem' }}>
          Oborové hodnoty jsou interní odhad M&amp;Ai pro e-commerce, nejde o statistiku z trhu.
        </p>
      )}
    </div>
  );
}

// ── Kvalita byznysu ────────────────────────────────────────────────────────────
function BQBars({ bq }) {
  const dims = [
    { key: 'customer_quality',     label: 'Zákaznická kvalita',     desc: 'RRS, opakované nákupy, retence' },
    { key: 'margin_quality',       label: 'Maržová stabilita',       desc: 'EBITDA a hrubá marže' },
    { key: 'acquisition_quality',  label: 'Efektivita akvizice',     desc: 'CAC, ROAS, akvizční kanály' },
    { key: 'growth_quality',       label: 'Kvalita růstu',            desc: 'YoY trend tržeb' },
    { key: 'business_model',       label: 'Obchodní model',           desc: 'Typ produktu, logistika' },
    { key: 'transferability',      label: 'Převoditelnost',           desc: 'Závislost na zakladateli' },
    { key: 'inventory_management', label: 'Řízení skladu',            desc: 'Obrátkovost zásob' },
    { key: 'concentration_risk',   label: 'Koncentrační riziko',      desc: 'Produktová a dodavatelská závislost' },
  ];
  const total = Math.round(bq.total * 100);
  const totalColor = total >= 70 ? 'var(--color-success)' : total >= 50 ? 'var(--gold)' : 'var(--color-danger)';
  const totalLabel = total >= 70 ? 'Nadprůměrná kvalita' : total >= 50 ? 'Průměrná kvalita' : 'Podprůměrná kvalita';

  return (
    <div style={{ marginBottom: '1.75rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
        <h3 style={{ fontFamily: "'Source Serif 4', Georgia, serif", fontWeight: 600, fontSize: '1.05rem', color: 'var(--star-white)', margin: 0 }}>Kvalita byznysu</h3>
        <span style={{ fontWeight: 800, fontSize: '1rem', color: totalColor }}>
          {total} / 100 <span style={{ fontSize: '0.78rem', fontWeight: 400, color: 'var(--text-dim)' }}>- {totalLabel}</span>
        </span>
      </div>
      {/* Dimenze, které se u typu podnikání nehodnotí (null), se nezobrazují. */}
      {dims.filter(d => bq.dimensions[d.key] != null).map(d => {
        const val = bq.dimensions[d.key];
        const pct = Math.round(val * 100);
        const barColor = pct >= 70 ? 'var(--color-success)' : pct >= 40 ? 'var(--gold)' : 'var(--color-danger)';
        return (
          <div key={d.key} style={{ marginBottom: '0.6rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.83rem', marginBottom: '0.2rem' }}>
              <span style={{ color: 'var(--star-white)' }}>{d.label}</span>
              <span style={{ fontWeight: 700, color: barColor }}>{pct} %</span>
            </div>
            <div style={{ background: 'var(--purple-deep)', borderRadius: 4, height: 7 }}>
              <div style={{ width: `${pct}%`, height: '100%', background: barColor, borderRadius: 4, transition: 'width 0.5s ease' }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

// Naléhavost se značí štítkem verzálkami, jak ji značí oceňovací zpráva.
// Barevné kolečko vedle nadpisu působilo jako chatovací aplikace, ne jako
// podklad, který klient ukáže kupujícímu.
const STITEK_ZAKLAD = {
  fontFamily: "'Inter', sans-serif", fontWeight: 600, fontSize: '0.62rem',
  letterSpacing: '0.18em', textTransform: 'uppercase', whiteSpace: 'nowrap',
};
const PRIORITA = {
  vysoka:  { ...STITEK_ZAKLAD, color: 'var(--color-danger)' },
  stredni: { ...STITEK_ZAKLAD, color: 'var(--gold)' },
  nizka:   { ...STITEK_ZAKLAD, color: 'var(--color-success)' },
};
const POPIS_PRIORITY = { vysoka: 'vysoká', stredni: 'střední', nizka: 'nízká' };

/**
 * Jeden řádek můstku od hodnoty provozu k hodnotě pro majitele.
 *
 * Klient musí vidět, kde peníze ubyly. Kdyby se hodnota jen tiše snížila,
 * vypadalo by to jako chyba výpočtu, zatímco takhle si každý řádek najde
 * ve vlastní rozvaze.
 */
function RadekMustku({ popis, castka, pozn, souhrn }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
      gap: '1rem', padding: '0.4rem 0',
      borderTop: souhrn ? '1px solid var(--purple-mid)' : 'none',
      marginTop: souhrn ? '0.35rem' : 0,
    }}>
      <span>
        <span style={{
          fontSize: souhrn ? '0.95rem' : '0.88rem',
          color: souhrn ? 'var(--star-white)' : 'var(--text-dim)',
          fontWeight: souhrn ? 600 : 400,
        }}>{popis}</span>
        {pozn && (
          <span style={{ display: 'block', fontSize: '0.76rem', color: 'var(--text-dim)' }}>{pozn}</span>
        )}
      </span>
      <span style={{
        fontFamily: "'Source Serif 4', Georgia, serif",
        fontSize: souhrn ? '1.25rem' : '1rem',
        color: souhrn ? 'var(--gold)' : 'var(--star-white)',
        whiteSpace: 'nowrap',
      }}>
        {castka < 0 ? '−' : ''}{fmt(Math.abs(castka))} Kč
      </span>
    </div>
  );
}

// ── Doporučení ─────────────────────────────────────────────────────────────────
function Recommendations({ problems, bq, metrics }) {
  const recs = [];

  if (bq?.dimensions?.customer_quality != null && bq.dimensions.customer_quality < 0.6)
    recs.push({
      priority: 'vysoka',
      title: 'Zvyšte podíl opakujících se zákazníků',
      detail: `Vaše zákaznická základna má prostor pro zlepšení. Implementujte post-purchase e-mail flow, věrnostní program a automatizaci připomínek k opakovaným nákupům, každé 1 % RRS navíc přidá ${fmt(metrics?.per_1pct_rrs_value || 0)} Kč k hodnotě firmy.`,
    });

  if (bq?.dimensions?.acquisition_quality != null && bq.dimensions.acquisition_quality < 0.5)
    recs.push({
      priority: 'vysoka',
      title: 'Diverzifikujte akvizici zákazníků',
      detail: 'Vysoká závislost na placené reklamě zvyšuje náklady a snižuje stabilitu byznysu. Investujte do SEO, content marketingu a budování e-mailové databáze, organické kanály jsou pro valuaci klíčové.',
    });

  if (bq?.dimensions?.transferability < 0.5)
    recs.push({
      priority: 'stredni',
      title: 'Snižte závislost na zakladateli',
      detail: 'Byznys závislý na jedné klíčové osobě je pro investory méně atraktivní. Dokumentujte procesy, delegujte rozhodování a vybudujte tým schopný fungovat bez zakladatele.',
    });

  if (metrics?.inventory_turnover && metrics.inventory_turnover < 4)
    recs.push({
      priority: 'stredni',
      title: 'Optimalizujte skladové zásoby',
      detail: `Obrátkovost zásob ${fmtX(metrics.inventory_turnover)} je pod doporučenou hranicí. Spusťte flash sale na pomalu obrátkové produkty, zkraťte dodací lhůty a nastavte min/max skladové limity.`,
    });

  if (metrics?.ebitda_margin_pct < 10)
    recs.push({
      priority: 'stredni',
      title: 'Zvyšte EBITDA maržemi',
      detail: `EBITDA marže ${fmtPct(metrics.ebitda_margin_pct)} je pod hranicí 10 %. Projděte strukturu COGS (vyjednání lepších cen u dodavatelů) a OPEX (optimalizace marketingových výdajů).`,
    });

  if (recs.length === 0)
    recs.push({
      priority: 'nizka',
      title: 'Udržte konzistentní růst a retenci',
      detail: 'Vaše klíčové metriky jsou na dobré úrovni. Fokusujte se na škálování stávajících kanálů a pravidelné sledování RRS a EBITDA marže.',
    });

  return (
    <div style={{ marginBottom: '1.75rem' }}>
      <h3 style={{ fontFamily: "'Source Serif 4', Georgia, serif", fontWeight: 600, fontSize: '1.05rem', color: 'var(--star-white)', marginBottom: '0.75rem' }}>Doporučení ke zvýšení hodnoty</h3>
      {recs.map((r, i) => (
        <div key={i} style={{ padding: '0.85rem 1rem', borderLeft: '3px solid var(--purple-glow)', background: 'var(--purple-deep)', borderRadius: '0 10px 10px 0', marginBottom: '0.6rem' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.6rem', marginBottom: '0.2rem' }}>
            <span style={PRIORITA[r.priority] || PRIORITA.stredni}>{POPIS_PRIORITY[r.priority] || 'střední'}</span>
            <span style={{ fontWeight: 700, fontSize: '0.9rem' }}>{r.title}</span>
          </div>
          <div style={{ fontSize: '0.84rem', color: 'var(--star-white)', lineHeight: 1.5 }}>{r.detail}</div>
        </div>
      ))}
    </div>
  );
}

// ── Rizikové faktory ───────────────────────────────────────────────────────────
const SEV_LABEL = { critical: 'Kritické', high: 'Vysoké', medium: 'Střední' };
const SEV_COLOR = { critical: 'var(--color-danger)', high: 'var(--gold)', medium: 'var(--gold)' };
const SEV_BG    = { critical: 'rgba(192,69,63,0.08)', high: 'rgba(184,147,45,0.10)', medium: 'rgba(184,147,45,0.10)' };

function ProblemsPanel({ problems }) {
  if (!problems?.length) {
    return (
      <div style={{ background: 'rgba(47,143,91,0.08)', border: '1px solid rgba(47,143,91,0.45)', borderRadius: 10, padding: '1rem 1.25rem', marginBottom: '1.5rem', color: 'var(--color-success)', fontSize: '0.88rem' }}>
        Nebyly detekovány žádné kritické rizikové faktory.
      </div>
    );
  }
  return (
    <div style={{ marginBottom: '1.75rem' }}>
      <h3 style={{ fontFamily: "'Source Serif 4', Georgia, serif", fontWeight: 600, fontSize: '1.05rem', color: 'var(--color-danger)', marginBottom: '0.75rem' }}>Rizikové faktory ({problems.length})</h3>
      {problems.map((p, i) => (
        <div key={i} style={{ background: SEV_BG[p.severity], border: '1px solid', borderColor: SEV_COLOR[p.severity] + '55', borderRadius: 10, padding: '0.85rem 1rem', marginBottom: '0.5rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.3rem' }}>
            <span style={{ fontWeight: 700, color: SEV_COLOR[p.severity], fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              {SEV_LABEL[p.severity] || p.severity} riziko
            </span>
          </div>
          <div style={{ fontWeight: 700, fontSize: '0.9rem', marginBottom: '0.25rem', color: 'var(--star-white)' }}>{p.name_cs}</div>
          <div style={{ fontSize: '0.82rem', color: 'var(--text-dim)', marginBottom: '0.2rem' }}>Zjistili jsme: {p.trigger}</div>
          <div style={{ fontSize: '0.83rem', color: 'var(--star-white)' }}>Doporučení: {p.action_cs}</div>
        </div>
      ))}
    </div>
  );
}

// ── Valuation Growth Plan ──────────────────────────────────────────────────────
function LeverCard({ title, icon, current, impact1Label, impact1Val, impact5Label, impact5Val, example, color, bg }) {
  return (
    <div style={{ background: bg, border: `1px solid ${color}33`, borderRadius: 12, padding: '1rem 1.25rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.35rem' }}>
        <span style={{ fontSize: '1.15rem' }}>{icon}</span>
        <span style={{ fontWeight: 700, fontSize: '0.95rem', color: 'var(--star-white)' }}>{title}</span>
      </div>
      <div style={{ fontSize: '0.82rem', color: 'var(--text-dim)', marginBottom: '0.75rem', lineHeight: 1.45 }}>{current}</div>
      <div style={{ display: 'flex', gap: '0.65rem', marginBottom: '0.75rem', flexWrap: 'wrap' }}>
        <div style={{ background: 'white', border: `1px solid ${color}33`, borderRadius: 8, padding: '0.45rem 0.85rem', flex: 1, minWidth: 140 }}>
          <div style={{ fontSize: '0.71rem', color: 'var(--text-dim)', marginBottom: '0.15rem' }}>{impact1Label}</div>
          <div style={{ fontWeight: 800, color, fontSize: '0.92rem' }}>{impact1Val}</div>
        </div>
        <div style={{ background: 'white', border: `1px solid ${color}33`, borderRadius: 8, padding: '0.45rem 0.85rem', flex: 1, minWidth: 140 }}>
          <div style={{ fontSize: '0.71rem', color: 'var(--text-dim)', marginBottom: '0.15rem' }}>{impact5Label}</div>
          <div style={{ fontWeight: 800, color, fontSize: '0.92rem' }}>{impact5Val}</div>
        </div>
      </div>
      <div style={{ fontSize: '0.79rem', color: 'var(--text-dim)', lineHeight: 1.5, borderTop: `1px solid ${color}22`, paddingTop: '0.6rem' }}>
        <span style={{ fontWeight: 600 }}>Příklad z praxe: </span>{example}
      </div>
    </div>
  );
}

function ValuationGrowthPlan({ v, metrics, problems, scenarios }) {
  // Efektivní valuační násobitel (bez hodnoty zásob)
  const effectiveMultiple = v.ebitda > 0 ? (v.total - (v.inventory_value || 0)) / v.ebitda : 0;
  // Implicitní revenue z EBITDA marže (revenue = ebitda / margin%)
  const impliedRevenue = metrics.ebitda_margin_pct > 0 ? v.ebitda / (metrics.ebitda_margin_pct / 100) : 0;
  // Dopad +1 % EBITDA marže na valuaci = (revenue × 0.01) × násobitel
  const per1PctEbitda = Math.round(impliedRevenue * 0.01 * effectiveMultiple);

  // Celkový rizikový discount z detekovaných problémů
  const totalRiskImpactPct = problems.reduce((sum, p) => sum + Math.abs(p.valuation_impact_pct || 0), 0);
  const riskValueImpact = Math.round(totalRiskImpactPct * v.total);

  return (
    <div style={{ marginBottom: '1.75rem' }}>
      <h3 style={{ fontFamily: "'Source Serif 4', Georgia, serif", fontWeight: 600, fontSize: '1.05rem', color: 'var(--star-white)', marginBottom: '0.35rem' }}>Jak zvýšit hodnotu vaší firmy</h3>
      <p style={{ fontSize: '0.84rem', color: 'var(--text-dim)', marginBottom: '1rem', lineHeight: 1.5 }}>
        Hodnota firmy není fixní číslo, závisí na konkrétních ukazatelích, které můžete aktivně ovlivnit.
        Níže vidíte páky s největším dopadem a co reálně přinesou při zlepšení.
      </p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '1.5rem' }}>

        <LeverCard
          title="Opakující se zákazníci (RRS)"
          current={`Aktuálně ${fmtPct(v.rrs_pct, 0)} tržeb pochází od zákazníků, kteří se vrátili a nakoupili znovu. RRS je nejvýznamnější faktor valuace, firmy s vysokým RRS jsou oceňovány výrazně vyšším násobitelem, protože mají předvídatelný, stabilní revenue. Průměr e-commerce je 30-45 %.`}
          impact1Label="+1 % RRS → nárůst hodnoty"
          impact1Val={`+${fmt(v.per_1pct_rrs_value)} Kč`}
          impact5Label="+5 % RRS → nárůst hodnoty"
          impact5Val={`+${fmt(v.per_1pct_rrs_value * 5)} Kč`}
          example={`Firma prodávající kosmetiku zavedla post-purchase email sekvenci (3 e-maily po nákupu) a SMS připomínku s personalizovanou nabídkou po 30 dnech. RRS vzrostl z 29 % na 37 % za 6 měsíců, bez navýšení marketingového rozpočtu. Hodnota firmy se zvýšila o ${fmt(v.per_1pct_rrs_value * 8)} Kč při stejném obratu.`}
          color="var(--purple-glow)"
          bg="var(--purple-deep)"
        />

        {per1PctEbitda > 0 && (
          <LeverCard
            title="EBITDA marže"
              current={`Aktuální EBITDA marže je ${fmtPct(metrics.ebitda_margin_pct)}, to znamená, že z každých 100 Kč tržeb zůstane ${fmtPct(metrics.ebitda_margin_pct)} jako provozní zisk před odpisy. Každé 1 % zlepšení (snížením COGS nebo OPEX) přímo násobí hodnotu firmy přes valuační násobitel ${effectiveMultiple.toFixed(1)}×.`}
            impact1Label="+1 % EBITDA marže → nárůst hodnoty"
            impact1Val={`+${fmt(per1PctEbitda)} Kč`}
            impact5Label="+5 % EBITDA marže → nárůst hodnoty"
            impact5Val={`+${fmt(per1PctEbitda * 5)} Kč`}
            example={`Firma s doplňky stravy přejednala podmínky s hlavním dodavatelem a snížil COGS o 3,5 %. Zároveň zrušil 4 zbytečné SaaS nástroje (úspora 220 tis. Kč/rok). EBITDA marže vzrostla z ${fmtPct(metrics.ebitda_margin_pct)} na ${fmtPct(metrics.ebitda_margin_pct + 4)}, hodnota firmy se zvýšila o ${fmt(per1PctEbitda * 4)} Kč.`}
            color="var(--purple-glow)"
            bg="var(--purple-deep)"
          />
        )}

        {riskValueImpact > 0 && (
          <LeverCard
            title="Eliminace rizikových faktorů"
              current={`Detekováno ${problems.length} ${problems.length === 1 ? 'rizikový faktor' : problems.length < 5 ? 'rizikové faktory' : 'rizikových faktorů'}. Rizika nesnižují jen samotnou valuaci, způsobují, že kupující nebo investor požaduje vyšší discount nebo odkládá rozhodnutí. Každé odstraněné riziko přímo zvyšuje důvěryhodnost firmy a tím i její cenu.`}
            impact1Label="Odhadovaný dopad na valuaci"
            impact1Val={`−${fmt(riskValueImpact)} Kč`}
            impact5Label="Potenciál při eliminaci rizik"
            impact5Val={`+${fmt(riskValueImpact)} Kč`}
            example={`Firma závislá na jednom dodavateli byla při due diligence oceněn s diskontem 20 %, kupující nechtěl platit plnou cenu za firmu, která může přestat fungovat, pokud jeden dodavatel zdraží nebo přestane dodávat. Po diverzifikaci na 3 dodavatele discount zmizel a hodnota firmy vzrostla o 1,8M Kč při nezměněném EBITDA.`}
            color="var(--color-danger)"
            bg="rgba(192,69,63,0.08)"
          />
        )}
      </div>

      {scenarios?.length > 0 && (
        <>
          <div style={{ fontSize: '0.83rem', fontWeight: 600, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.5rem' }}>
            Simulace hodnoty při různých scénářích
          </div>
          <p style={{ fontSize: '0.82rem', color: 'var(--text-dim)', marginBottom: '0.75rem', lineHeight: 1.5 }}>
            Tyto scénáře ukazují, jak by vypadala hodnota firmy při kombinovaném zlepšení RRS, marže a tempa růstu, tři různé trajektorie podle ambice:
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: '0.65rem' }}>
            {scenarios.map((s, i) => (
              <div key={i} style={{ background: 'rgba(47,143,91,0.08)', border: '1px solid rgba(47,143,91,0.45)', borderRadius: 10, padding: '1rem' }}>
                <div style={{ fontWeight: 700, fontSize: '0.87rem', color: 'var(--star-white)', marginBottom: '0.3rem', lineHeight: 1.35 }}>{s.name_cs}</div>
                <div style={{ fontSize: '0.78rem', color: 'var(--text-dim)', marginBottom: '0.65rem', lineHeight: 1.4 }}>{s.detail_cs}</div>
                <div style={{ color: 'var(--color-success)', fontWeight: 900, fontSize: '1.1rem' }}>+{s.uplift_pct} %</div>
                <div style={{ fontSize: '0.9rem', fontWeight: 700, color: 'var(--star-white)' }}>{fmt(s.new_valuation)} Kč</div>
                <div style={{ fontSize: '0.75rem', color: 'var(--color-success)', marginTop: '0.1rem' }}>+{fmt(s.new_valuation - v.total)} Kč oproti aktuálnímu</div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ── Investor View ──────────────────────────────────────────────────────────────
function InvestorView({ aiReport, loading }) {
  const inv = aiReport?.investor_view;

  if (!inv) {
    return (
      <div style={{ marginBottom: '1.75rem', padding: '1.5rem', background: 'var(--purple-deep)', border: '1px solid var(--purple-mid)', borderRadius: 12, textAlign: 'center' }}>
        {loading ? (
          <>
            <div style={{
              width: 32, height: 32, margin: '0 auto 0.75rem',
              border: '4px solid var(--purple-mid)', borderTopColor: 'var(--purple-glow)',
              borderRadius: '50%', animation: 'spin 0.9s linear infinite',
            }} />
            <div style={{ fontWeight: 700, color: 'var(--star-white)', marginBottom: '0.25rem', fontSize: '0.92rem' }}>Generuji pohled investora...</div>
            <div style={{ color: 'var(--text-dim)', fontSize: '0.82rem' }}>Expertní analýza se připravuje. Může trvat 1-5 minut.</div>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </>
        ) : (
          <div style={{ color: 'var(--text-dim)', fontSize: '0.87rem' }}>Pohled investora se vygeneruje společně s expertním reportem. Klikněte na "Přegenerovat" v sekci níže.</div>
        )}
      </div>
    );
  }

  const gradeCfg = {
    'A+': { color: 'var(--color-success)', bg: 'rgba(47,143,91,0.08)', border: 'var(--color-success)', label: 'Prémiová investiční příležitost' },
    'A':  { color: 'var(--color-success)', bg: 'rgba(47,143,91,0.08)', border: 'rgba(47,143,91,0.45)', label: 'Výborná investiční příležitost' },
    'B+': { color: 'var(--purple-glow)', bg: 'var(--purple-deep)', border: 'var(--purple-bright)', label: 'Nadstandardní investiční příležitost' },
    'B':  { color: 'var(--purple-glow)', bg: 'var(--purple-deep)', border: 'var(--purple-bright)', label: 'Dobrá investiční příležitost' },
    'C+': { color: 'var(--gold)', bg: 'rgba(184,147,45,0.12)', border: 'rgba(184,147,45,0.35)', label: 'Průměrná, potenciál k růstu' },
    'C':  { color: 'var(--gold)', bg: 'rgba(184,147,45,0.12)', border: 'rgba(184,147,45,0.35)', label: 'Podmínečně zajímavá' },
    'D':  { color: 'var(--color-danger)', bg: 'rgba(192,69,63,0.08)', border: 'rgba(192,69,63,0.45)', label: 'Vyžaduje výrazné zlepšení' },
  };
  const gc = gradeCfg[inv.investment_grade] || gradeCfg['C'];

  const Section = ({ icon, title, items, color, bg }) => (
    <div style={{ background: bg || 'var(--purple-deep)', border: `1px solid ${color}33`, borderRadius: 10, padding: '0.9rem 1.1rem' }}>
      <div style={{ fontWeight: 700, fontSize: '0.85rem', color, marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
        <span>{icon}</span> {title}
      </div>
      <ul style={{ margin: 0, paddingLeft: '1.1rem', display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
        {(items || []).map((item, i) => (
          <li key={i} style={{ fontSize: '0.82rem', color: 'var(--star-white)', lineHeight: 1.5 }}>{item}</li>
        ))}
      </ul>
    </div>
  );

  return (
    <div style={{ marginBottom: '1.75rem' }}>
      <h3 style={{ fontFamily: "'Source Serif 4', Georgia, serif", fontWeight: 600, fontSize: '1.05rem', color: 'var(--star-white)', marginBottom: '0.35rem' }}>Pohled investora</h3>
      <p style={{ fontSize: '0.84rem', color: 'var(--text-dim)', marginBottom: '1rem', lineHeight: 1.5 }}>
        Jak by vaši firmu hodnotil zkušený investor nebo strategický kupující při prvním setkání.
        Tato sekce simuluje pohled zvenčí, bez emocí, čistě na základě dat.
      </p>

      {/* Investment grade + verdikt */}
      <div style={{ background: gc.bg, border: `2px solid ${gc.border}`, borderRadius: 14, padding: '1.25rem 1.5rem', marginBottom: '1.25rem', display: 'flex', gap: '1.25rem', alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div style={{ textAlign: 'center', minWidth: 64 }}>
          <div style={{ fontFamily: "'Source Serif 4', Georgia, serif", fontSize: '3rem', fontWeight: 400, color: gc.color, lineHeight: 1 }}>{inv.investment_grade}</div>
          <div style={{ fontSize: '0.68rem', color: 'var(--text-dim)', marginTop: '0.2rem', whiteSpace: 'nowrap' }}>Investment Grade</div>
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: '0.95rem', color: gc.color, marginBottom: '0.25rem' }}>{gc.label}</div>
          {(inv.grade_explanation || inv.grade_rationale) && (
            <div style={{ fontSize: '0.81rem', color: 'var(--text-dim)', marginBottom: '0.6rem', lineHeight: 1.45, fontStyle: 'italic' }}>
              {inv.grade_explanation || inv.grade_rationale}
            </div>
          )}
          <div style={{ fontSize: '0.84rem', color: 'var(--star-white)', lineHeight: 1.55 }}>
            <span style={{ fontWeight: 600, color: 'var(--text-dim)' }}>Verdikt: </span>
            {inv.overall_verdict}
          </div>
        </div>
      </div>

      {/* 4 sekce */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.65rem' }}>
        <Section
          title="Co mě přitahuje"
          items={inv.strengths}
          color="var(--color-success)"
          bg="rgba(47,143,91,0.08)"
        />
        <Section
          title="Co mě znepokojuje"
          items={inv.risks}
          color="var(--gold)"
          bg="rgba(184,147,45,0.12)"
        />
        <Section
          title="Co by snížilo cenu"
          items={inv.value_destroyers}
          color="var(--color-danger)"
          bg="rgba(192,69,63,0.08)"
        />
        <Section
          title="Za co bych zaplatil více"
          items={inv.value_drivers}
          color="var(--purple-glow)"
          bg="var(--purple-deep)"
        />
      </div>
    </div>
  );
}

// ── Exit Readiness Score ────────────────────────────────────────────────────────
function ExitReadinessScore({ bq }) {
  const d = bq?.dimensions || {};

  // Výpočet 5 kategorií z BQ dimenzí (0-1 → 0-100)
  const score = (keys) => {
    // Nehodnocené dimenze (null) do průměru nevstupují.
    const vals = keys.map(k => d[k]).filter(x => x != null);
    if (!vals.length) return null;
    return Math.round((vals.reduce((a, b) => a + b, 0) / vals.length) * 100);
  };

  const categories = [
    {
      key: 'finance',
      label: 'Finanční kvalita',
      score: score(['margin_quality', 'acquisition_quality']),
      weight: 0.25,
      icon: '',
      what: 'Jak zdravé jsou marže a efektivita akvizice zákazníků, kupující prověří tato čísla jako první při due diligence.',
      fix: 'Zaměřte se na snížení COGS (renegociace dodavatelů) a optimalizaci marketingových nákladů (CAC).',
    },
    {
      key: 'revenue',
      label: 'Stabilita revenue',
      score: score(['customer_quality', 'growth_quality']),
      weight: 0.30,
      icon: '',
      what: 'Jak předvídatelný a stabilní je příjem, firmy s vysokým RRS a rostoucím trendem mají snazší exit a vyšší valuaci.',
      fix: 'Zvyšte podíl opakujících se zákazníků přes email marketing, věrnostní program a automatizaci post-purchase flow.',
    },
    {
      key: 'transfer',
      label: 'Přenositelnost firmy',
      score: score(['transferability']),
      weight: 0.20,
      icon: '',
      what: 'Jak dobře firma funguje bez zakladatele, závislost na jedné osobě je nejčastější důvod selhání nebo odložení exitů.',
      fix: 'Zdokumentujte procesy, delegujte klíčová rozhodnutí a zapojte management do každodenního řízení firmy.',
    },
    {
      key: 'risk',
      label: 'Rizikový profil',
      score: score(['concentration_risk']),
      weight: 0.15,
      icon: '',
      what: 'Jak koncentrovaná jsou rizika, produktová a dodavatelská závislost snižuje kupní cenu, protože kupující cení diverzifikaci.',
      fix: 'Diverzifikujte dodavatele (minimum 3) a rozložte produktové portfolio tak, aby top 3 produkty tvořily max. 40 % tržeb.',
    },
    {
      key: 'scale',
      label: 'Škálovatelnost',
      score: score(['growth_quality', 'business_model', 'inventory_management']),
      weight: 0.10,
      icon: '',
      what: 'Jak snadno může firma růst bez proporcionálního navyšování nákladů, kupující kupuje potenciál, ne jen historii.',
      fix: 'Optimalizujte sklady a logistiku, přejděte na vlastní značku nebo private label, automatizujte opakující se procesy.',
    },
  ];

  // Celkové vážené skóre
  const totalScore = Math.round(
    categories.reduce((sum, c) => sum + c.score * c.weight, 0)
  );

  const totalCfg = totalScore >= 75
    ? { label: 'Připravena na exit', color: 'var(--color-success)', bg: 'rgba(47,143,91,0.08)', border: 'rgba(47,143,91,0.45)' }
    : totalScore >= 60
    ? { label: 'Dobrá základna pro exit', color: 'var(--purple-glow)', bg: 'var(--purple-deep)', border: 'var(--purple-bright)' }
    : totalScore >= 40
    ? { label: 'Potřebuje přípravu před exitem', color: 'var(--gold)', bg: 'rgba(184,147,45,0.12)', border: 'rgba(184,147,45,0.35)' }
    : { label: 'Firma není připravena na exit', color: 'var(--color-danger)', bg: 'rgba(192,69,63,0.08)', border: 'rgba(192,69,63,0.45)' };

  return (
    <div style={{ marginBottom: '1.75rem' }}>
      <h3 style={{ fontFamily: "'Source Serif 4', Georgia, serif", fontWeight: 600, fontSize: '1.05rem', color: 'var(--star-white)', marginBottom: '0.35rem' }}>Exit Readiness Score</h3>
      <p style={{ fontSize: '0.84rem', color: 'var(--text-dim)', marginBottom: '1rem', lineHeight: 1.5 }}>
        Exit Readiness Score měří, jak je vaše firma připravena na prodej nebo příjem investora.
        Nízké skóre neznamená, že firma není hodnotná, znamená, že bez přípravy pravděpodobně
        nedostanete nabídku odpovídající její skutečné hodnotě.
      </p>

      {/* Celkové skóre */}
      <div style={{ background: totalCfg.bg, border: `2px solid ${totalCfg.border}`, borderRadius: 14, padding: '1.25rem 1.5rem', marginBottom: '1.25rem', display: 'flex', alignItems: 'center', gap: '1.5rem', flexWrap: 'wrap' }}>
        <div style={{ textAlign: 'center', minWidth: 80 }}>
          <div style={{ fontFamily: "'Source Serif 4', Georgia, serif", fontSize: '2.8rem', fontWeight: 400, color: totalCfg.color, lineHeight: 1 }}>{totalScore}</div>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-dim)', marginTop: '0.2rem' }}>ze 100</div>
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: '1rem', color: totalCfg.color, marginBottom: '0.35rem' }}>{totalCfg.label}</div>
          <div style={{ fontSize: '0.82rem', color: 'var(--text-dim)', lineHeight: 1.5 }}>
            <span style={{ fontWeight: 600 }}>Příklad z praxe: </span>
            {totalScore >= 75
              ? 'Firma s tímto skóre typicky projde due diligence bez výrazných komplikací a dostane nabídku blízko střední valuaci nebo nad ní. Kupující vnímá firmu jako připravenou, procesně řízenou a přenositelnou.'
              : totalScore >= 60
              ? 'Firma s tímto skóre má solidní základnu, ale due diligence obvykle odhalí 2-3 oblasti, které kupující použije k vyjednání slevy 10-20 %. Cílená příprava po dobu 6-12 měsíců může výrazně zlepšit výsledek.'
              : totalScore >= 40
              ? 'Firmy s tímto skóre čelí při prodeji výraznému diskontu, kupující požaduje slevu 20-35 % oproti střední valuaci nebo odkládá rozhodnutí. Před snahou o exit je vhodná 12-18 měsíců příprava.'
              : 'Firmy s tímto skóre mají při snaze o exit velmi těžkou pozici, kupující buď nabídne výrazně podhodnocenou cenu, nebo odejde kvůli identifikovaným rizikům. Doporučujeme nejprve adresovat klíčové slabiny.'}
          </div>
        </div>
      </div>

      {/* Kategorie */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.65rem' }}>
        {categories.map(c => {
          const barColor = c.score >= 70 ? 'var(--color-success)' : c.score >= 45 ? 'var(--gold)' : 'var(--color-danger)';
          const showFix  = c.score < 60;
          return (
            <div key={c.key} style={{ border: '1px solid var(--purple-mid)', borderRadius: 10, padding: '0.85rem 1rem', background: 'var(--purple-deep)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.4rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                  <span style={{ fontSize: '1rem' }}>{c.icon}</span>
                  <span style={{ fontWeight: 700, fontSize: '0.88rem', color: 'var(--star-white)' }}>{c.label}</span>
                  <span style={{ fontSize: '0.72rem', color: 'var(--text-dim)' }}>({Math.round(c.weight * 100)} % váha)</span>
                </div>
                <span style={{ fontWeight: 800, fontSize: '1rem', color: barColor }}>{c.score} / 100</span>
              </div>
              {/* Progress bar */}
              <div style={{ background: 'var(--purple-mid)', borderRadius: 4, height: 6, marginBottom: '0.5rem' }}>
                <div style={{ width: `${c.score}%`, height: '100%', background: barColor, borderRadius: 4 }} />
              </div>
              <div style={{ fontSize: '0.8rem', color: 'var(--text-dim)', lineHeight: 1.45 }}>{c.what}</div>
              {showFix && (
                <div style={{ marginTop: '0.4rem', fontSize: '0.79rem', color: 'var(--gold)', background: 'rgba(184,147,45,0.12)', borderRadius: 6, padding: '0.3rem 0.6rem', lineHeight: 1.45 }}>
                  ↳ {c.fix}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Breakdown valuace ──────────────────────────────────────────────────────────
function ValuationBreakdown({ v }) {
  const rows = [
    { label: 'EBITDA', value: `${fmt(v.ebitda)} Kč`, note: 'Tržby − COGS − OPEX' },
    { label: 'Násobek', value: `${String(v.effective_multiple).replace('.', ',')}×`, note: 'medián trhu pro velikost firmy, upravený o kvalitu podnikání' },
    { label: '', value: '', note: '' },
    { label: 'HODNOTA PROVOZU', value: `${fmt(v.total)} Kč`, note: 'EBITDA × násobek, před úvěry a hotovostí', bold: true },
  ];
  return (
    <div style={{ marginBottom: '1.75rem' }}>
      <h3 style={{ fontFamily: "'Source Serif 4', Georgia, serif", fontWeight: 600, fontSize: '1.05rem', color: 'var(--star-white)', marginBottom: '0.75rem' }}>Detail výpočtu</h3>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.87rem' }}>
        <tbody>
          {rows.map((r, i) => (
            r.label ? (
              <tr key={i} style={{ borderBottom: '1px solid var(--purple-deep)', background: r.bold ? 'var(--purple-deep)' : 'transparent' }}>
                <td style={{ padding: '0.5rem 0.75rem', color: 'var(--star-white)', fontWeight: r.bold ? 700 : 400 }}>{r.label}</td>
                <td style={{ padding: '0.5rem 0.75rem', fontWeight: r.bold ? 800 : 600, textAlign: 'right', color: r.bold ? 'var(--purple-glow)' : 'var(--star-white)', fontSize: r.bold ? '1rem' : 'inherit' }}>{r.value}</td>
                <td style={{ padding: '0.5rem 0.75rem', color: 'var(--text-dim)', fontSize: '0.78rem' }}>{r.note}</td>
              </tr>
            ) : <tr key={i}><td colSpan={3} style={{ padding: '0.2rem' }} /></tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Audit: modul karta ─────────────────────────────────────────────────────────
const SEV_AUDIT_COLOR  = { critical: 'var(--color-danger)', high: 'var(--gold)', medium: 'var(--gold)', low: 'var(--purple-glow)', ok: 'var(--color-success)' };
const SEV_AUDIT_BG     = { critical: 'rgba(192,69,63,0.08)', high: 'rgba(184,147,45,0.10)', medium: 'rgba(184,147,45,0.10)', low: 'var(--purple-deep)',  ok: 'rgba(47,143,91,0.08)'  };
const SEV_AUDIT_LABEL  = { critical: 'Kritické', high: 'Vysoké', medium: 'Střední', low: 'Nízké', ok: 'V pořádku' };

function AuditModuleCard({ m }) {
  const color = SEV_AUDIT_COLOR[m.severity] || 'var(--text-dim)';
  const bg    = SEV_AUDIT_BG[m.severity]    || 'var(--purple-deep)';
  return (
    <div style={{ border: `1px solid ${color}44`, borderRadius: 12, overflow: 'hidden', marginBottom: '0.75rem' }}>
      <div style={{ background: bg, padding: '0.75rem 1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <span style={{ fontWeight: 700, fontSize: '0.95rem', color: 'var(--star-white)' }}>{m.name}</span>
          <span style={{ marginLeft: '0.6rem', fontSize: '0.75rem', fontWeight: 700, color, background: `${color}18`, padding: '0.15rem 0.5rem', borderRadius: 999 }}>
            {SEV_AUDIT_LABEL[m.severity] || m.severity}
          </span>
        </div>
        {m.lost_profit > 0 && (
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontWeight: 800, fontSize: '1rem', color }}>{fmt(m.lost_profit)} Kč</div>
            <div style={{ fontSize: '0.72rem', color: 'var(--text-dim)' }}>ztracený zisk / rok</div>
          </div>
        )}
      </div>
      {(m.problems?.length > 0 || m.recommendations?.length > 0) && (
        <div style={{ padding: '0.75rem 1rem', background: 'white', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
          {m.problems?.length > 0 && (
            <div>
              <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.3rem' }}>Zjistili jsme</div>
              {m.problems.map((p, i) => (
                <div key={i} style={{ fontSize: '0.82rem', color: 'var(--star-white)', marginBottom: '0.2rem', display: 'flex', gap: '0.35rem' }}>
                  <span style={{ color, flexShrink: 0 }}>▸</span><span>{p}</span>
                </div>
              ))}
            </div>
          )}
          {m.recommendations?.length > 0 && (
            <div>
              <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.3rem' }}>Doporučení</div>
              {m.recommendations.map((r, i) => (
                <div key={i} style={{ fontSize: '0.82rem', color: 'var(--star-white)', marginBottom: '0.2rem', display: 'flex', gap: '0.35rem' }}>
                  <span style={{ color: 'var(--color-success)', flexShrink: 0 }}>✓</span><span>{r}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Audit: prioritní akce ──────────────────────────────────────────────────────
function AuditPriorityActions({ actions }) {
  const diffColor = { snadné: 'var(--color-success)', střední: 'var(--gold)', náročné: 'var(--color-danger)' };
  return (
    <div style={{ marginBottom: '1.75rem' }}>
      <h3 style={{ fontFamily: "'Source Serif 4', Georgia, serif", fontWeight: 600, fontSize: '1.05rem', color: 'var(--star-white)', marginBottom: '0.75rem' }}>Prioritní akce, seřazeno podle dopadu</h3>
      {actions.map(a => (
        <div key={a.rank} style={{ display: 'grid', gridTemplateColumns: '2rem 1fr auto', gap: '0.75rem', alignItems: 'start', padding: '0.75rem', background: 'var(--purple-deep)', borderRadius: 10, marginBottom: '0.5rem', border: '1px solid var(--purple-deep)' }}>
          <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'var(--star-white)', color: 'white', fontWeight: 900, fontSize: '0.85rem', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            {a.rank}
          </div>
          <div>
            <div style={{ fontSize: '0.87rem', fontWeight: 600, color: 'var(--star-white)', marginBottom: '0.15rem' }}>{a.action}</div>
            <div style={{ fontSize: '0.78rem', color: 'var(--text-dim)' }}>{a.timeframe} · obtížnost: <span style={{ color: diffColor[a.difficulty] || 'var(--star-white)', fontWeight: 600 }}>{a.difficulty}</span></div>
          </div>
          <div style={{ textAlign: 'right', flexShrink: 0 }}>
            <div style={{ fontWeight: 800, fontSize: '0.9rem', color: 'var(--color-success)' }}>+{fmt(a.expected_kc)} Kč</div>
            <div style={{ fontSize: '0.72rem', color: 'var(--text-dim)' }}>dopad / rok</div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Audit: Expertní analýza ────────────────────────────────────────────────────
function AuditAIReport({ ai }) {
  if (!ai || ai.error) return null;
  return (
    <div style={{ marginBottom: '1.75rem' }}>
      {/* Executive summary */}
      <div style={{ background: 'linear-gradient(135deg, var(--purple-glow) 0%, var(--purple-glow) 100%)', borderRadius: 12, padding: '1.5rem', marginBottom: '1.25rem', color: 'white' }}>
        <div style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.06em', opacity: 0.75, marginBottom: '0.5rem' }}>Executive Summary</div>
        <p style={{ margin: 0, fontSize: '0.93rem', lineHeight: 1.65, opacity: 0.95 }}>{ai.executive_summary}</p>
      </div>

      {/* SWOT */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '1.25rem' }}>
        {[
          { key: 'strengths',     label: 'Silné stránky',  icon: '', bg: 'rgba(47,143,91,0.08)', border: 'rgba(47,143,91,0.45)', color: 'var(--color-success)' },
          { key: 'weaknesses',    label: 'Slabé stránky',  icon: '', bg: 'rgba(184,147,45,0.10)', border: 'rgba(184,147,45,0.45)', color: 'var(--gold)' },
          { key: 'opportunities', label: 'Příležitosti',   icon: '', bg: 'var(--purple-deep)', border: 'var(--purple-bright)', color: 'var(--star-white)' },
          { key: 'threats',       label: 'Hrozby',         icon: '', bg: 'rgba(192,69,63,0.08)', border: 'rgba(192,69,63,0.45)', color: 'var(--color-danger)' },
        ].map(s => (
          <div key={s.key} style={{ background: s.bg, border: `1px solid ${s.border}`, borderRadius: 10, padding: '0.85rem' }}>
            <div style={{ fontWeight: 700, fontSize: '0.82rem', color: s.color, marginBottom: '0.5rem' }}>{s.icon} {s.label}</div>
            {(ai[s.key] || []).map((item, i) => (
              <div key={i} style={{ fontSize: '0.82rem', color: 'var(--star-white)', marginBottom: '0.25rem', display: 'flex', gap: '0.35rem', lineHeight: 1.4 }}>
                <span style={{ flexShrink: 0, color: s.color }}>•</span><span>{item}</span>
              </div>
            ))}
          </div>
        ))}
      </div>

      {/* Akční plán */}
      {ai.action_plan?.length > 0 && (
        <div style={{ marginBottom: '1.25rem' }}>
          <h4 style={{ fontSize: '0.95rem', color: 'var(--star-white)', marginBottom: '0.6rem' }}>Akční plán</h4>
          {ai.action_plan.map((a, i) => (
            <div key={i} style={{ display: 'flex', gap: '0.75rem', padding: '0.65rem 0.85rem', background: 'var(--purple-deep)', borderRadius: 8, marginBottom: '0.4rem', alignItems: 'flex-start', border: '1px solid var(--purple-deep)' }}>
              <span style={{ fontWeight: 900, color: 'var(--purple-glow)', fontSize: '0.85rem', flexShrink: 0, minWidth: '1.2rem' }}>{a.priorita}.</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: '0.87rem', color: 'var(--star-white)' }}>{a.akce}</div>
                <div style={{ fontSize: '0.78rem', color: 'var(--text-dim)', marginTop: '0.1rem' }}>{a.dopad} · {a.casovy_ramec}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Full report */}
      {ai.full_report && (
        <div style={{ background: 'var(--purple-deep)', border: '1px solid var(--purple-mid)', borderRadius: 10, padding: '1.25rem' }}>
          <h4 style={{ fontSize: '0.95rem', color: 'var(--star-white)', marginBottom: '0.75rem' }}>Detailní zpráva</h4>
          <div style={{ fontSize: '0.87rem', color: 'var(--star-white)', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>{ai.full_report}</div>
        </div>
      )}
    </div>
  );
}

// ── Valuace expertní report: karta metriky (rozbalitelná) ──────────────────────
function AIReportMetricCard({ section }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ border: '1px solid var(--purple-mid)', borderRadius: 10, marginBottom: '0.5rem', overflow: 'hidden' }}>
      <button
        onClick={() => setOpen(!open)}
        style={{ width: '100%', textAlign: 'left', padding: '0.8rem 1rem', background: open ? 'var(--purple-deep)' : 'var(--purple-deep)', border: 'none', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
      >
        <span style={{ fontWeight: 700, fontSize: '0.88rem', color: 'var(--star-white)' }}>{section.title}</span>
        <span style={{ color: 'var(--purple-glow)', fontSize: '0.8rem', flexShrink: 0 }}>{open ? '▲ Sbalit' : '▼ Rozbalit'}</span>
      </button>
      {open && (
        <div style={{ padding: '0.9rem 1rem', borderTop: '1px solid var(--purple-mid)', display: 'flex', flexDirection: 'column', gap: '0.65rem' }}>
          <div>
            <div style={{ fontSize: '0.71rem', fontWeight: 700, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.25rem' }}>Co to znamená pro váš byznys</div>
            <div style={{ fontSize: '0.86rem', color: 'var(--star-white)', lineHeight: 1.65 }}>{section.what_it_means}</div>
          </div>
          <div>
            <div style={{ fontSize: '0.71rem', fontWeight: 700, color: 'var(--purple-glow)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.25rem' }}>Proč na tom záleží</div>
            <div style={{ fontSize: '0.86rem', color: 'var(--star-white)', lineHeight: 1.65 }}>{section.why_it_matters}</div>
          </div>
          <div style={{ background: 'rgba(47,143,91,0.08)', border: '1px solid rgba(47,143,91,0.35)', borderRadius: 8, padding: '0.7rem 0.85rem' }}>
            <div style={{ fontSize: '0.71rem', fontWeight: 700, color: 'var(--color-success)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.25rem' }}>Jak to zlepšit</div>
            <div style={{ fontSize: '0.86rem', color: 'var(--star-white)', lineHeight: 1.65 }}>{section.how_to_improve}</div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Valuace expertní report: sekce ───────────────────────────────────────────
function ValuationAISection({ aiReport, onGenerate, loading, error, tier }) {
  const hasReport = aiReport && aiReport.tier === tier;
  return (
    <div style={{ marginBottom: '1.75rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.85rem', flexWrap: 'wrap', gap: '0.75rem' }}>
        <h3 style={{ fontFamily: "'Source Serif 4', Georgia, serif", fontWeight: 600, fontSize: '1.05rem', color: 'var(--star-white)', margin: 0 }}>Expertní výkladový report</h3>
        {hasReport && (
          <button onClick={onGenerate} disabled={loading} style={{ background: 'none', border: '1px solid var(--purple-mid)', borderRadius: 8, padding: '0.35rem 0.8rem', fontSize: '0.78rem', color: 'var(--text-dim)', cursor: 'pointer' }}>
            ↺ Přegenerovat
          </button>
        )}
      </div>

      {error && <div className="error-message" style={{ marginBottom: '0.75rem' }}>Chyba: {error}</div>}

      {loading && (
        <div style={{ textAlign: 'center', padding: '2.5rem 2rem', background: 'linear-gradient(135deg, var(--purple-deep) 0%, var(--purple-deep) 100%)', borderRadius: 16, border: '2px solid var(--purple-mid)' }}>
          <div style={{
            width: 48, height: 48, margin: '0 auto 1rem',
            border: '5px solid var(--purple-mid)', borderTopColor: 'var(--purple-glow)',
            borderRadius: '50%', animation: 'spin 0.9s linear infinite',
          }} />
          <div style={{ fontWeight: 800, fontSize: '1.1rem', color: 'var(--star-white)', marginBottom: '0.5rem' }}>Zpracováváme vaše data a generujeme expertní analýzu</div>
          <div style={{ fontSize: '0.9rem', color: 'var(--text-dim)', lineHeight: 1.6, maxWidth: 500, margin: '0 auto', marginBottom: '0.75rem' }}>
            Analyzujeme finanční data, KPI metriky a kvalitu byznysu. Na základě toho připravujeme personalizovaný výkladový report včetně pohledu investora a doporučení.
          </div>
          <div style={{ fontSize: '0.82rem', color: 'var(--text-dim)', background: 'var(--purple-deep)', borderRadius: 8, padding: '0.5rem 1rem', display: 'inline-block' }}>
            Generování může trvat 1-5 minut. Prosím neopouštějte tuto stránku.
          </div>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      )}

      {!hasReport && !loading && !error && (
        <div style={{ padding: '1.5rem', background: 'rgba(184,147,45,0.10)', border: '1px solid rgba(184,147,45,0.45)', borderRadius: 10, textAlign: 'center' }}>
          <div style={{ fontSize: '0.92rem', fontWeight: 700, color: 'var(--gold)', marginBottom: '0.35rem' }}>Expertní report se nepodařilo vygenerovat</div>
          <div style={{ fontSize: '0.84rem', color: 'var(--text-dim)', marginBottom: '0.75rem' }}>Zkuste to prosím znovu.</div>
          <button onClick={onGenerate} className="btn-primary">Vygenerovat report znovu</button>
        </div>
      )}

      {hasReport && (
        <>
          {/* Celkové hodnocení */}
          <div style={{ background: 'linear-gradient(135deg, var(--purple-glow) 0%, var(--purple-glow) 100%)', borderRadius: 12, padding: '1.4rem', marginBottom: '1.1rem', color: 'white' }}>
            <div style={{ fontSize: '0.73rem', textTransform: 'uppercase', letterSpacing: '0.06em', opacity: 0.75, marginBottom: '0.4rem' }}>Celkové hodnocení</div>
            <p style={{ margin: 0, fontSize: '0.93rem', lineHeight: 1.7, opacity: 0.97 }}>{aiReport.overall_assessment}</p>
          </div>

          {/* Metriky */}
          {aiReport.sections?.length > 0 && (
            <div style={{ marginBottom: '1.1rem' }}>
              <div style={{ fontSize: '0.82rem', color: 'var(--text-dim)', marginBottom: '0.5rem' }}>Kliknutím na metriku zobrazíte podrobný výklad:</div>
              {aiReport.sections.map((s, i) => <AIReportMetricCard key={i} section={s} />)}
            </div>
          )}

          {/* Top priority, Value Creation */}
          {aiReport.top_priorities?.value_creation?.length > 0 && (
            <div style={{ marginBottom: '1.1rem' }}>
              <h4 style={{ fontSize: '0.9rem', color: 'var(--star-white)', marginBottom: '0.15rem' }}>Zvýšení hodnoty společnosti</h4>
              <p style={{ fontSize: '0.82rem', color: 'var(--text-dim)', marginBottom: '0.6rem', margin: '0 0 0.6rem' }}>Akce zvyšující valuační násobitel, důležité pro prodej firmy nebo vstup investora</p>
              {aiReport.top_priorities.value_creation.map((p, i) => (
                <div key={i} style={{ padding: '1rem', background: 'var(--purple-deep)', border: '1px solid var(--purple-bright)', borderRadius: 10, marginBottom: '0.6rem' }}>
                  <div style={{ fontWeight: 700, fontSize: '0.9rem', color: 'var(--star-white)', marginBottom: '0.4rem' }}>
                    {i === 0 ? '' : ''} {p.title}
                  </div>
                  <div style={{ fontSize: '0.84rem', color: 'var(--star-white)', marginBottom: '0.35rem', lineHeight: 1.55 }}><strong>Situace:</strong> {p.problem}</div>
                  {p.why_it_affects_value && <div style={{ fontSize: '0.84rem', color: 'var(--star-white)', marginBottom: '0.35rem', lineHeight: 1.55 }}><strong>Proč to ovlivňuje hodnotu:</strong> {p.why_it_affects_value}</div>}
                  <div style={{ fontSize: '0.84rem', color: 'var(--star-white)', marginBottom: '0.5rem', lineHeight: 1.55 }}><strong>Dopad:</strong> {p.impact}</div>
                  {p.action_steps?.length > 0 && (
                    <div style={{ background: 'rgba(47,143,91,0.08)', border: '1px solid rgba(47,143,91,0.35)', borderRadius: 8, padding: '0.65rem 0.85rem' }}>
                      <div style={{ fontSize: '0.71rem', fontWeight: 700, color: 'var(--color-success)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.25rem' }}>Jak to udělat</div>
                      {p.action_steps.map((step, j) => (
                        <div key={j} style={{ fontSize: '0.84rem', color: 'var(--star-white)', lineHeight: 1.6, marginBottom: '0.15rem', display: 'flex', gap: '0.4rem' }}>
                          <span style={{ color: 'var(--color-success)', fontWeight: 700, flexShrink: 0 }}>{j + 1}.</span><span>{step}</span>
                        </div>
                      ))}
                      {p.timeline && <div style={{ fontSize: '0.78rem', color: 'var(--text-dim)', marginTop: '0.4rem' }}>Časový rámec: {p.timeline}</div>}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Top priority, Profit Acceleration */}
          {aiReport.top_priorities?.profit_acceleration?.length > 0 && (
            <div style={{ marginBottom: '1.1rem' }}>
              <h4 style={{ fontSize: '0.9rem', color: 'var(--color-success)', marginBottom: '0.15rem' }}>Okamžitý dopad na zisk</h4>
              <p style={{ fontSize: '0.82rem', color: 'var(--text-dim)', marginBottom: '0.6rem', margin: '0 0 0.6rem' }}>Akce zvyšující měsíční cash flow, důležité pro každodenní provoz</p>
              {aiReport.top_priorities.profit_acceleration.map((p, i) => (
                <div key={i} style={{ padding: '1rem', background: 'rgba(47,143,91,0.08)', border: '1px solid rgba(47,143,91,0.45)', borderRadius: 10, marginBottom: '0.6rem' }}>
                  <div style={{ fontWeight: 700, fontSize: '0.9rem', color: 'var(--star-white)', marginBottom: '0.4rem' }}>
                    {i === 0 ? '' : ''} {p.title}
                  </div>
                  <div style={{ fontSize: '0.84rem', color: 'var(--star-white)', marginBottom: '0.35rem', lineHeight: 1.55 }}><strong>Situace:</strong> {p.problem}</div>
                  {p.how_it_makes_money && <div style={{ fontSize: '0.84rem', color: 'var(--star-white)', marginBottom: '0.35rem', lineHeight: 1.55 }}><strong>Jak to přinese peníze:</strong> {p.how_it_makes_money}</div>}
                  <div style={{ fontSize: '0.84rem', color: 'var(--star-white)', marginBottom: '0.5rem', lineHeight: 1.55 }}><strong>Dopad:</strong> {p.impact}</div>
                  {p.action_steps?.length > 0 && (
                    <div style={{ background: 'var(--purple-deep)', border: '1px solid var(--purple-bright)', borderRadius: 8, padding: '0.65rem 0.85rem' }}>
                      <div style={{ fontSize: '0.71rem', fontWeight: 700, color: 'var(--purple-glow)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.25rem' }}>Jak to udělat</div>
                      {p.action_steps.map((step, j) => (
                        <div key={j} style={{ fontSize: '0.84rem', color: 'var(--star-white)', lineHeight: 1.6, marginBottom: '0.15rem', display: 'flex', gap: '0.4rem' }}>
                          <span style={{ color: 'var(--purple-glow)', fontWeight: 700, flexShrink: 0 }}>{j + 1}.</span><span>{step}</span>
                        </div>
                      ))}
                      {p.timeline && <div style={{ fontSize: '0.78rem', color: 'var(--text-dim)', marginTop: '0.4rem' }}>Časový rámec: {p.timeline}{p.implementation_cost ? ` · Náklady: ${p.implementation_cost}` : ''}</div>}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* PRO: Rizika */}
          {tier === 'pro' && aiReport.risk_explanations?.length > 0 && (
            <div style={{ marginBottom: '1.1rem' }}>
              <h4 style={{ fontSize: '0.9rem', color: 'var(--color-danger)', marginBottom: '0.6rem' }}>Výklad rizikových faktorů</h4>
              {aiReport.risk_explanations.map((r, i) => (
                <div key={i} style={{ padding: '0.85rem 1rem', background: 'rgba(192,69,63,0.08)', border: '1px solid rgba(192,69,63,0.35)', borderRadius: 10, marginBottom: '0.5rem' }}>
                  <div style={{ fontWeight: 700, fontSize: '0.87rem', color: 'var(--color-danger)', marginBottom: '0.3rem' }}>{r.risk}</div>
                  <div style={{ fontSize: '0.84rem', color: 'var(--star-white)', marginBottom: '0.3rem', lineHeight: 1.55 }}><strong>Co to znamená:</strong> {r.what_it_means}</div>
                  {r.financial_impact && <div style={{ fontSize: '0.84rem', color: 'var(--star-white)', marginBottom: '0.3rem', lineHeight: 1.55 }}><strong>Finanční dopad:</strong> {r.financial_impact}</div>}
                  {r.how_buyer_sees_it && <div style={{ fontSize: '0.84rem', color: 'var(--star-white)', marginBottom: '0.3rem', lineHeight: 1.55 }}><strong>Pohled kupujícího:</strong> {r.how_buyer_sees_it}</div>}
                  {r.mitigation_steps?.length > 0 && (
                    <div style={{ background: 'var(--purple-dark)', borderRadius: 8, padding: '0.5rem 0.75rem', marginTop: '0.4rem' }}>
                      <div style={{ fontSize: '0.71rem', fontWeight: 700, color: 'var(--color-success)', textTransform: 'uppercase', marginBottom: '0.25rem' }}>Jak riziko snížit</div>
                      {r.mitigation_steps.map((step, j) => (
                        <div key={j} style={{ fontSize: '0.82rem', color: 'var(--star-white)', marginBottom: '0.15rem', display: 'flex', gap: '0.4rem' }}>
                          <span style={{ color: 'var(--color-success)', fontWeight: 700, flexShrink: 0 }}>{j + 1}.</span><span>{step}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {r.timeline_to_fix && <div style={{ fontSize: '0.78rem', color: 'var(--text-dim)', marginTop: '0.35rem' }}>Odhad doby řešení: {r.timeline_to_fix}</div>}
                </div>
              ))}
            </div>
          )}

          {/* PRO: Simulace růstu, průvodce */}
          {tier === 'pro' && aiReport.growth_explanations?.length > 0 && (
            <div style={{ marginBottom: '1.1rem' }}>
              <h4 style={{ fontSize: '0.9rem', color: 'var(--star-white)', marginBottom: '0.6rem' }}>Jak dosáhnout scénářů růstu</h4>
              {aiReport.growth_explanations.map((g, i) => (
                <div key={i} style={{ padding: '0.85rem 1rem', background: 'rgba(47,143,91,0.08)', border: '1px solid rgba(47,143,91,0.45)', borderRadius: 10, marginBottom: '0.5rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.35rem', flexWrap: 'wrap', gap: '0.5rem' }}>
                    <div style={{ fontWeight: 700, fontSize: '0.87rem', color: 'var(--color-success)' }}>{g.scenario}</div>
                    {g.probability_assessment && (
                      <span style={{ fontSize: '0.72rem', fontWeight: 600, color: 'var(--text-dim)', background: 'var(--purple-mid)', padding: '0.15rem 0.5rem', borderRadius: 999 }}>
                        {g.probability_assessment}
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: '0.84rem', color: 'var(--star-white)', marginBottom: '0.5rem', lineHeight: 1.55 }}>{g.what_needs_to_happen}</div>
                  {g.key_actions?.length > 0 && (
                    <div style={{ marginBottom: '0.5rem' }}>
                      {g.key_actions.map((step, j) => (
                        <div key={j} style={{ fontSize: '0.83rem', color: 'var(--star-white)', marginBottom: '0.2rem', display: 'flex', gap: '0.4rem', lineHeight: 1.5 }}>
                          <span style={{ color: 'var(--color-success)', fontWeight: 700, flexShrink: 0 }}>{j + 1}.</span><span>{step}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {g.what_could_go_wrong && (
                    <div style={{ fontSize: '0.82rem', color: 'var(--gold)', background: 'rgba(184,147,45,0.12)', borderRadius: 6, padding: '0.4rem 0.6rem', marginBottom: '0.3rem', lineHeight: 1.45 }}>
                      <strong>Riziko:</strong> {g.what_could_go_wrong}
                    </div>
                  )}
                  {g.milestone_check && (
                    <div style={{ fontSize: '0.78rem', color: 'var(--text-dim)', marginTop: '0.2rem' }}>Milník za 90 dnů: {g.milestone_check}</div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* PRO: Implementační roadmapa */}
          {tier === 'pro' && aiReport.implementation_roadmap && (
            <div style={{ marginBottom: '1.1rem' }}>
              <h4 style={{ fontSize: '0.9rem', color: 'var(--star-white)', marginBottom: '0.6rem' }}>Implementační roadmapa</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                {[
                  { label: 'Příštích 30 dnů', text: aiReport.implementation_roadmap.next_30_days, color: 'var(--color-success)', bg: 'rgba(47,143,91,0.08)', border: 'rgba(47,143,91,0.45)', icon: '' },
                  { label: 'Příštích 90 dnů', text: aiReport.implementation_roadmap.next_90_days, color: 'var(--purple-glow)', bg: 'var(--purple-deep)', border: 'var(--purple-bright)', icon: '' },
                  { label: 'Příštích 180 dnů', text: aiReport.implementation_roadmap.next_180_days, color: 'var(--purple-glow)', bg: 'var(--purple-deep)', border: 'var(--purple-mid)', icon: '' },
                ].map(r => r.text && (
                  <div key={r.label} style={{ background: r.bg, border: `1px solid ${r.border}`, borderRadius: 10, padding: '0.85rem 1rem' }}>
                    <div style={{ fontWeight: 700, fontSize: '0.85rem', color: r.color, marginBottom: '0.3rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                      <span>{r.icon}</span> {r.label}
                    </div>
                    <div style={{ fontSize: '0.84rem', color: 'var(--star-white)', lineHeight: 1.6 }}>{r.text}</div>
                  </div>
                ))}
                {aiReport.implementation_roadmap.key_milestone && (
                  <div style={{ background: 'rgba(184,147,45,0.10)', border: '1px solid rgba(184,147,45,0.45)', borderRadius: 10, padding: '0.85rem 1rem' }}>
                    <div style={{ fontWeight: 700, fontSize: '0.85rem', color: 'var(--gold)', marginBottom: '0.3rem' }}>Klíčový milník (6 měsíců)</div>
                    <div style={{ fontSize: '0.84rem', color: 'var(--star-white)', lineHeight: 1.6 }}>{aiReport.implementation_roadmap.key_milestone}</div>
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── Audit: detailní průvodce akcemi ────────────────────────────────────────────
function AuditActionDetails({ actions, actionDetails }) {
  if (!actionDetails?.length) return null;
  return (
    <div style={{ marginBottom: '1.75rem' }}>
      <h3 style={{ fontFamily: "'Source Serif 4', Georgia, serif", fontWeight: 600, fontSize: '1.05rem', color: 'var(--star-white)', marginBottom: '0.75rem' }}>Detailní průvodce, jak každou akci provést</h3>
      {actionDetails.map((d, i) => {
        const action = actions.find(a => a.rank === d.rank);
        return (
          <div key={i} style={{ border: '1px solid var(--purple-mid)', borderRadius: 12, marginBottom: '0.75rem', overflow: 'hidden' }}>
            <div style={{ background: 'var(--purple-deep)', padding: '0.8rem 1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                <div style={{ width: 26, height: 26, borderRadius: '50%', background: 'var(--star-white)', color: 'white', fontWeight: 900, fontSize: '0.8rem', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  {d.rank}
                </div>
                <span style={{ fontWeight: 700, fontSize: '0.9rem', color: 'var(--star-white)' }}>{action?.action || `Akce ${d.rank}`}</span>
              </div>
              {action && <span style={{ fontSize: '0.82rem', fontWeight: 700, color: 'var(--color-success)' }}>+{fmt(action.expected_kc)} Kč/rok · {action.timeframe}</span>}
            </div>
            <div style={{ padding: '0.9rem 1rem', background: 'white', display: 'flex', flexDirection: 'column', gap: '0.7rem' }}>
              <div>
                <div style={{ fontSize: '0.71rem', fontWeight: 700, color: 'var(--color-danger)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.25rem' }}>Problém</div>
                <div style={{ fontSize: '0.86rem', color: 'var(--star-white)', lineHeight: 1.65 }}>{d.problem_description}</div>
              </div>
              <div>
                <div style={{ fontSize: '0.71rem', fontWeight: 700, color: 'var(--gold)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.25rem' }}>Dopad na byznys</div>
                <div style={{ fontSize: '0.86rem', color: 'var(--star-white)', lineHeight: 1.65 }}>{d.business_impact}</div>
              </div>
              {d.how_to_fix?.length > 0 && (
                <div>
                  <div style={{ fontSize: '0.71rem', fontWeight: 700, color: 'var(--color-success)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.3rem' }}>Jak to opravit, krok za krokem</div>
                  {d.how_to_fix.map((step, j) => (
                    <div key={j} style={{ fontSize: '0.85rem', color: 'var(--star-white)', marginBottom: '0.3rem', display: 'flex', gap: '0.5rem', lineHeight: 1.55 }}>
                      <span style={{ color: 'var(--color-success)', fontWeight: 700, flexShrink: 0, minWidth: '1.1rem' }}>{j + 1}.</span>
                      <span>{step}</span>
                    </div>
                  ))}
                </div>
              )}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
                <div style={{ background: 'rgba(47,143,91,0.08)', borderRadius: 8, padding: '0.6rem 0.75rem' }}>
                  <div style={{ fontSize: '0.71rem', fontWeight: 700, color: 'var(--color-success)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.2rem' }}>Jak poznat úspěch</div>
                  <div style={{ fontSize: '0.82rem', color: 'var(--star-white)', lineHeight: 1.5 }}>{d.success_metrics}</div>
                </div>
                <div style={{ background: 'var(--purple-deep)', borderRadius: 8, padding: '0.6rem 0.75rem' }}>
                  <div style={{ fontSize: '0.71rem', fontWeight: 700, color: 'var(--star-white)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.2rem' }}>Kdo to má řídit</div>
                  <div style={{ fontSize: '0.82rem', color: 'var(--star-white)', lineHeight: 1.5 }}>{d.who_should_own}</div>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Audit sekce ────────────────────────────────────────────────────────────────
function AuditSection({ auditData, onRun, loading, error }) {
  const mods = auditData?.modules;
  return (
    <div style={{ marginBottom: '1.5rem' }}>
      {/* Hlavička */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem', flexWrap: 'wrap', gap: '0.75rem' }}>
        <div>
          <h3 style={{ fontFamily: "'Source Serif 4', Georgia, serif", fontWeight: 600, fontSize: '1.12rem', color: 'var(--star-white)', margin: 0 }}>Audit podniku</h3>
          <p style={{ fontSize: '0.85rem', color: 'var(--text-dim)', margin: '0.25rem 0 0' }}>Analýza ztraceného zisku a personalizovaný konzultantský report</p>
        </div>
        {auditData && (
          <button
            className="btn-primary"
            onClick={onRun}
            disabled={loading}
            style={{ minWidth: 180 }}
          >
            ↺ Znovu spustit audit
          </button>
        )}
      </div>

      {error && (
        <div className="error-message" style={{ marginBottom: '1rem' }}>Chyba: {error}</div>
      )}

      {loading && (
        <div style={{ textAlign: 'center', padding: '2.5rem', background: 'var(--purple-deep)', borderRadius: 12, border: '1px solid var(--purple-mid)' }}>
          <div style={{ fontSize: '1.5rem', marginBottom: '0.75rem' }}></div>
          <div style={{ fontWeight: 700, color: 'var(--star-white)', marginBottom: '0.4rem' }}>Generuji audit...</div>
          <div style={{ fontSize: '0.87rem', color: 'var(--text-dim)' }}>Analyzujeme data a připravujeme konzultantský report. Může trvat 20-40 sekund.</div>
        </div>
      )}

      {!auditData && !loading && !error && (
        <div style={{ padding: '1.25rem', background: 'var(--purple-deep)', border: '1px solid var(--purple-mid)', borderRadius: 10, textAlign: 'center' }}>
          <div style={{ fontSize: '0.87rem', color: 'var(--text-dim)' }}>Spouštím audit podniku...</div>
        </div>
      )}

      {auditData && (
        <>
          {/* Celkový ztracený zisk */}
          <div style={{ background: 'linear-gradient(135deg, var(--color-danger) 0%, var(--color-danger) 100%)', borderRadius: 12, padding: '1.25rem 1.5rem', marginBottom: '1.5rem', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem' }}>
            <div>
              <div style={{ fontSize: '0.8rem', opacity: 0.8, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.2rem' }}>Odhadovaný ztracený zisk</div>
              <div style={{ fontFamily: "'Source Serif 4', Georgia, serif", fontSize: '2.2rem', fontWeight: 400 }}>{fmt(auditData.lost_profit_total)} Kč / rok</div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '0.8rem', opacity: 0.8 }}>podíl na tržbách</div>
              <div style={{ fontFamily: "'Source Serif 4', Georgia, serif", fontSize: '1.8rem', fontWeight: 400 }}>{auditData.lost_profit_pct_of_revenue?.toFixed(1)} %</div>
            </div>
          </div>

          {/* Audit moduly */}
          <h3 style={{ fontFamily: "'Source Serif 4', Georgia, serif", fontWeight: 600, fontSize: '1.05rem', color: 'var(--star-white)', marginBottom: '0.75rem' }}>Výsledky audit modulů</h3>
          {mods && Object.values(mods).map(m => <AuditModuleCard key={m.key} m={m} />)}

          {/* Prioritní akce */}
          {auditData.priority_actions?.length > 0 && (
            <AuditPriorityActions actions={auditData.priority_actions} />
          )}

          {/* Expertní analýza + detailní průvodce */}
          {auditData.ai_report && !auditData.ai_report.error && (
            <>
              <h3 style={{ fontFamily: "'Source Serif 4', Georgia, serif", fontWeight: 600, fontSize: '1.05rem', color: 'var(--star-white)', marginBottom: '0.75rem' }}>Konzultantský report</h3>
              <AuditAIReport ai={auditData.ai_report} />
              <AuditActionDetails
                actions={auditData.priority_actions || []}
                actionDetails={auditData.ai_report.action_details}
              />
            </>
          )}
        </>
      )}
    </div>
  );
}

// ── Priority Action Plan ───────────────────────────────────────────────────────
function PriorityActionPlan({ problems, aiReport, v }) {
  // top_priorities je objekt { value_creation: [...], profit_acceleration: [...] }
  const tp = aiReport?.top_priorities || {};
  const aiValueCreation = tp.value_creation || [];
  const aiProfitAcceleration = tp.profit_acceleration || [];

  // Seřadit problémy: critical → high → medium
  const sorted = [...problems].sort((a, b) => {
    const order = { critical: 0, high: 1, medium: 2 };
    return (order[a.severity] ?? 3) - (order[b.severity] ?? 3);
  });

  // Sestavit unified seznam akcí
  const actions = [];

  sorted.filter(p => p.severity === 'critical' || p.severity === 'high').forEach(p => {
    actions.push({
      kind: 'problem',
      severity: p.severity,
      title: p.name_cs,
      why: p.trigger,
      impact: p.valuation_impact_pct
        ? `Odhadovaný dopad na valuaci: −${fmt(Math.abs(p.valuation_impact_pct) * v.total)} Kč`
        : null,
      action: p.action_cs,
    });
  });

  aiValueCreation.forEach(p => {
    actions.push({
      kind: 'ai',
      severity: 'ai',
      title: p.title,
      why: p.problem,
      impact: p.impact,
      action: p.action_steps ? p.action_steps.join('; ') : '',
    });
  });

  aiProfitAcceleration.forEach(p => {
    actions.push({
      kind: 'ai',
      severity: 'ai',
      title: p.title,
      why: p.problem,
      impact: p.impact,
      action: p.action_steps ? p.action_steps.join('; ') : '',
    });
  });

  sorted.filter(p => p.severity === 'medium').forEach(p => {
    actions.push({
      kind: 'problem',
      severity: p.severity,
      title: p.name_cs,
      why: p.trigger,
      impact: p.valuation_impact_pct
        ? `Odhadovaný dopad na valuaci: −${fmt(Math.abs(p.valuation_impact_pct) * v.total)} Kč`
        : null,
      action: p.action_cs,
    });
  });

  const badgeCfg = {
    critical: { label: 'Kritické',        color: 'var(--color-danger)', bg: 'rgba(192,69,63,0.08)', headerBg: 'rgba(192,69,63,0.08)' },
    high:     { label: 'Vysoká priorita', color: 'var(--gold)', bg: 'rgba(184,147,45,0.10)', headerBg: 'rgba(184,147,45,0.10)' },
    medium:   { label: 'Střední priorita',color: 'var(--gold)', bg: 'rgba(184,147,45,0.10)', headerBg: 'rgba(184,147,45,0.10)' },
    ai:       { label: 'Expertní doporučení', color: 'var(--purple-glow)', bg: 'var(--purple-deep)', headerBg: 'var(--purple-deep)' },
  };

  if (!actions.length) {
    return (
      <div style={{ background: 'rgba(47,143,91,0.08)', border: '1px solid rgba(47,143,91,0.45)', borderRadius: 10, padding: '1rem 1.25rem', marginBottom: '1.75rem', color: 'var(--color-success)', fontSize: '0.88rem' }}>
        Žádné prioritní akce nebyly identifikovány. Firma je v dobré kondici.
      </div>
    );
  }

  return (
    <div style={{ marginBottom: '1.75rem' }}>
      <h3 style={{ fontFamily: "'Source Serif 4', Georgia, serif", fontWeight: 600, fontSize: '1.05rem', color: 'var(--star-white)', marginBottom: '0.35rem' }}>Prioritní akční plán</h3>
      <p style={{ fontSize: '0.84rem', color: 'var(--text-dim)', marginBottom: '1rem', lineHeight: 1.5 }}>
        Konkrétní akce seřazené podle dopadu na hodnotu firmy. Každá akce je přímou odpovědí
        na identifikovaný problém nebo nevyužitou příležitost, s jasným vysvětlením proč na tom záleží.
      </p>
      {actions.slice(0, 6).map((a, i) => {
        const bc = badgeCfg[a.severity] || badgeCfg.medium;
        return (
          <div key={i} style={{ border: `1px solid ${bc.color}33`, borderRadius: 12, marginBottom: '0.75rem', overflow: 'hidden' }}>
            {/* Hlavička */}
            <div style={{ background: bc.headerBg, padding: '0.75rem 1rem', display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
              <div style={{
                width: 30, height: 30, borderRadius: '50%',
                background: bc.color, color: 'white',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontWeight: 900, fontSize: '0.88rem', flexShrink: 0,
              }}>
                {i + 1}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: '0.92rem', color: 'var(--star-white)', marginBottom: '0.2rem' }}>{a.title}</div>
                <span style={{ fontSize: '0.7rem', fontWeight: 700, color: bc.color, background: `${bc.color}18`, padding: '0.1rem 0.5rem', borderRadius: 999 }}>
                  {bc.label}
                </span>
              </div>
              {a.impact && (
                <div style={{ fontSize: '0.8rem', color: bc.color, fontWeight: 700, textAlign: 'right', flexShrink: 0 }}>
                  {a.impact}
                </div>
              )}
            </div>
            {/* Tělo */}
            <div style={{ padding: '0.85rem 1rem', background: 'white' }}>
              <div style={{ fontSize: '0.82rem', color: 'var(--text-dim)', marginBottom: '0.55rem', lineHeight: 1.5 }}>
                <span style={{ fontWeight: 600, color: 'var(--star-white)' }}>Proč je to důležité: </span>{a.why}
              </div>
              <div style={{ fontSize: '0.82rem', color: 'var(--star-white)', lineHeight: 1.55 }}>
                <span style={{ fontWeight: 600 }}>Co udělat: </span>{a.action}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Hlavní komponenta ──────────────────────────────────────────────────────────
export function Step6_Results({ wizardState, setWizardState, onReset, onBack, bypassPayment = false }) {
  const result = wizardState.valuationResult;
  const isNegativeEbitda = result?.negative_ebitda === true;
  const plan = wizardState.selectedPlan || 'free';
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditError, setAuditError]     = useState(null);
  const [billingEnabled, setBillingEnabled] = useState(false);
  const [paidTiers, setPaidTiers]       = useState([]);
  const [checkoutBusy, setCheckoutBusy] = useState(false);
  const [upgradeLoading, setUpgradeLoading] = useState(false);
  const [upgradeStep, setUpgradeStep]   = useState('');
  const [pdfLoading, setPdfLoading]     = useState(false);
  const [platbyInfo, setPlatbyInfo]     = useState(false);
  const reportRef = useRef(null);

  const showPro   = plan === 'pro' || plan === 'komplet';
  // Audit stojí na oborových průměrech, které existují jen pro e-shopové
  // segmenty. U obchodu, výroby, služeb a ostatních by vrátil prázdné moduly
  // a e-shopová doporučení (3PL sklad, dopravci), proto se tam nenabízí,
  // dokud audit nebude srovnávat se vzorkem firem z rejstříku.
  const auditDostupny = [undefined, null, '', 'eshop'].includes(wizardState.typ_podnikani);
  const showAudit = auditDostupny && (plan === 'audit' || plan === 'komplet');

  // ── Upgrade z FREE na jiný plán (bez re-enteru dat) ──
  const handleUpgrade = async (targetPlan) => {
    setUpgradeLoading(true);
    const needsAI    = targetPlan === 'pro' || targetPlan === 'komplet';
    const needsAudit = targetPlan === 'audit' || targetPlan === 'komplet';

    try {
      // 1) Expertní report (PRO / KOMPLET), s retry
      if (needsAI && !wizardState.aiReport) {
        const MAX_RETRIES = 5;
        const RETRY_DELAYS = [3000, 6000, 12000, 20000];
        let aiSuccess = false;
        for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
          setUpgradeStep(
            attempt === 0
              ? 'Generuji expertní výkladový report...'
              : `Expertní report, pokus ${attempt + 1}/${MAX_RETRIES}...`
          );
          const aiResult = await generateAIReport(wizardState, 'pro');
          if (aiResult.success) {
            setWizardState(prev => ({ ...prev, aiReport: { tier: 'pro', ...aiResult.data } }));
            aiSuccess = true;
            break;
          }
          if (attempt + 1 < MAX_RETRIES) {
            const delay = RETRY_DELAYS[attempt] || 20000;
            setUpgradeStep(`Report se nepodařilo vygenerovat. Další pokus za ${delay / 1000}s...`);
            await new Promise(r => setTimeout(r, delay));
          }
        }
        if (!aiSuccess) {
          setUpgradeStep('Report se nepodařilo vygenerovat. Zkuste to znovu.');
        }
      }

      // 2) Audit (AUDIT / KOMPLET), s retry, ať po platbě nikdy nezůstane bez výsledku
      if (needsAudit && !wizardState.auditResult) {
        const MAX_RETRIES = 5;
        const RETRY_DELAYS = [3000, 6000, 12000, 20000];
        let auditOk = false;
        for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
          setUpgradeStep(attempt === 0
            ? 'Generuji konzultantský audit...'
            : `Audit, pokus ${attempt + 1}/${MAX_RETRIES}...`);
          const auditRes = await runAudit(wizardState);
          if (auditRes.success) {
            setWizardState(prev => ({ ...prev, auditResult: auditRes.data }));
            auditOk = true;
            break;
          }
          if (attempt + 1 < MAX_RETRIES) {
            const delay = RETRY_DELAYS[attempt] || 20000;
            setUpgradeStep(`Audit se nepodařilo vygenerovat. Další pokus za ${delay / 1000}s...`);
            await new Promise(r => setTimeout(r, delay));
          }
        }
        if (!auditOk) {
          setUpgradeStep('Audit se nepodařilo vygenerovat. Zkuste to prosím znovu, vaše platba zůstává platná.');
        }
      }

      // 3) Přepnout plán
      setWizardState(prev => ({ ...prev, selectedPlan: targetPlan }));
    } catch (e) {
      console.error('Upgrade failed:', e);
    }
    setUpgradeLoading(false);
    setUpgradeStep('');
  };

  // Načti stav plateb + na co už klient zaplatil PRO TUTO valuaci (kvůli bráně u "Odemknout")
  useEffect(() => {
    if (bypassPayment) return; // interní účet (admin/tým) platby neřeší
    getBillingStatus().then(s => setBillingEnabled(!!s.enabled)).catch(() => setBillingEnabled(false));
    // Entitlement výhradně pro tuto valuaci, jeden nákup neodemyká další valuace
    if (wizardState.savedValuationId) {
      getEntitlements(wizardState.savedValuationId).then(e => setPaidTiers(e.tiers || [])).catch(() => {});
    } else {
      setPaidTiers([]);
    }
  }, [bypassPayment, wizardState.savedValuationId]);

  // Výsledek zdarma je zúžený (omezeno). Jakmile je PRO odemčené, načti plný.
  const plnyRef = useRef(false);
  useEffect(() => {
    if (!showPro || !wizardState.valuationResult?.omezeno || plnyRef.current) return;
    plnyRef.current = true;
    calculateValuation(wizardState).then(r => {
      if (r.success && !r.data?.omezeno) {
        setWizardState(prev => ({ ...prev, valuationResult: r.data }));
      } else {
        plnyRef.current = false; // zkusí se znovu, až se změní stav (např. po zápisu platby)
      }
    });
  }, [showPro, wizardState.valuationResult, wizardState.savedValuationId, wizardState.aiReport, wizardState.auditResult]); // eslint-disable-line react-hooks/exhaustive-deps

  // Po návratu ze Stripe (App nastaví pendingGenerate) vygeneruj zaplacený report
  const pendingRef = useRef(false);
  useEffect(() => {
    const pg = wizardState.pendingGenerate;
    if (!pg || pendingRef.current) return;
    pendingRef.current = true;
    setWizardState(prev => { const n = { ...prev }; delete n.pendingGenerate; return n; });
    handleUpgrade(pg);
  // eslint-disable-line react-hooks/exhaustive-deps
  }, [wizardState.pendingGenerate]); // eslint-disable-line react-hooks/exhaustive-deps

  // Perzistence vygenerovaných reportů do DB (aby zůstaly u uložené valuace natrvalo)
  const persistedRef = useRef('');
  useEffect(() => {
    const id = wizardState.savedValuationId;
    if (!id) return;
    if (!wizardState.aiReport && !wizardState.auditResult) return;
    const sig = `${plan}|${wizardState.aiReport ? 1 : 0}|${wizardState.auditResult ? 1 : 0}`;
    if (persistedRef.current === sig) return;
    persistedRef.current = sig;
    updateValuation(id, {
      reports: { aiReport: wizardState.aiReport || null, auditResult: wizardState.auditResult || null, selectedPlan: plan },
      tier: plan,
    }).catch(() => { persistedRef.current = ''; /* zkusí se znovu při další změně */ });
  }, [wizardState.savedValuationId, wizardState.aiReport, wizardState.auditResult, plan]);

  // Brána: interní účet a zaplacený tier → generuj zdarma; jinak pošli na Stripe.
  // chargeTier = co reálně účtovat (u dokoupení jen chybějící část); generuje se targetPlan.
  const requestUpgrade = async (targetPlan, { chargeTier } = {}) => {
    if (!auditDostupny && (targetPlan === 'audit' || targetPlan === 'komplet')) return;
    if (bypassPayment || tierCovered(paidTiers, targetPlan)) {
      return handleUpgrade(targetPlan);
    }
    // Bez zapnutých plateb server placený obsah nevydá, tak ho ani nezkoušíme generovat.
    if (!billingEnabled) {
      setPlatbyInfo(true);
      return;
    }
    // Uložit stav, ať přežije přesměrování na Stripe a zpět
    setCheckoutBusy(true);
    try {
      localStorage.setItem('mnai_resume_purchase', JSON.stringify({ state: wizardState, tier: targetPlan, ts: Date.now() }));
      await startCheckout(chargeTier || targetPlan, wizardState.savedValuationId || null);
    } catch (e) {
      console.error('Checkout failed:', e);
      setCheckoutBusy(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (!reportRef.current) return;
    setPdfLoading(true);
    try {
      const labels = { free: 'FREE', pro: 'PRO', audit: 'AUDIT', komplet: 'KOMPLET' };
      const companySlug = (wizardState.company?.company_name || 'Report').replace(/[^a-zA-Z0-9áčďéěíňóřšťúůýžÁČĎÉĚÍŇÓŘŠŤÚŮÝŽ ]/g, '').replace(/\s+/g, '-');
      const filename = `Valuace-${companySlug}-${labels[plan] || 'Report'}-${new Date().toISOString().slice(0, 10)}.pdf`;
      await html2pdf()
        .set({
          margin: [10, 8, 10, 8],
          filename,
          image: { type: 'jpeg', quality: 0.98 },
          html2canvas: { scale: 2, useCORS: true, scrollY: 0, windowWidth: 900 },
          jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
          pagebreak: { mode: ['css'], avoid: ['tr', 'td', '.pdf-nobreak'] },
        })
        .from(reportRef.current)
        .save();
    } catch (e) {
      console.error('PDF generation failed:', e);
    }
    setPdfLoading(false);
  };

  const handleRunAudit = async () => {
    setAuditLoading(true);
    setAuditError(null);
    const res = await runAudit(wizardState);
    if (res.success) {
      setWizardState(prev => ({ ...prev, auditResult: res.data }));
    } else {
      setAuditError(res.error);
    }
    setAuditLoading(false);
  };

  // Automaticky spustit audit jen pro zaplacené AUDIT / KOMPLET (ne zdarma).
  // U záporné EBITDA se audit spouští až po zaplacení přes tlačítko (requestUpgrade).
  useEffect(() => {
    if (!result) return;
    if (showAudit && !wizardState.auditResult && !auditLoading) {
      handleRunAudit();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result]);

  if (!result || !result.valuation) {
    return (
      <div className="step-container">
        <h2>Výsledky valuace</h2>
        <div className="error-message">Výsledky nejsou k dispozici. Spusťte výpočet znovu.</div>
        <div className="button-group">
          <button onClick={onReset} className="btn-primary">Začít znovu</button>
        </div>
      </div>
    );
  }

  const v        = result.valuation;
  const seg      = result.segment;
  const metrics  = result.metrics;
  const bq       = result.business_quality;
  const fh       = result.financial_health;
  const problems = result.problems || [];
  const scenarios = result.growth_scenarios || [];

  const criticalProblem = problems.find(p => p.severity === 'critical');
  const aiReport = wizardState.aiReport?.tier === 'pro' ? wizardState.aiReport : null;

  // ── Upgrade loading overlay ──
  if (upgradeLoading) {
    return (
      <div className="step-container" style={{ textAlign: 'center' }}>
        <h2>Připravujeme vaši analýzu</h2>
        <div style={{ margin: '3rem auto', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1.5rem' }}>
          <div style={{
            width: 64, height: 64,
            border: '6px solid var(--purple-mid)', borderTopColor: 'var(--purple-glow)',
            borderRadius: '50%', animation: 'spin 0.9s linear infinite',
          }} />
          <p style={{ color: 'var(--text-dim)', fontSize: '1rem', fontStyle: 'italic' }}>{upgradeStep}</p>
          <p style={{ color: 'var(--text-dim)', fontSize: '0.85rem' }}>Může trvat 1-5 minut. Neopouštějte stránku.</p>
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div className="step-container">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
        <h2 style={{ margin: 0 }}>Výsledky valuace</h2>
        <button
          onClick={handleDownloadPdf}
          disabled={pdfLoading}
          style={{
            display: 'flex', alignItems: 'center', gap: '0.4rem',
            padding: '0.45rem 1rem', borderRadius: 8,
            border: '1px solid var(--purple-mid)', background: 'white',
            color: 'var(--star-white)', fontSize: '0.82rem', fontWeight: 600,
            cursor: pdfLoading ? 'not-allowed' : 'pointer',
            opacity: pdfLoading ? 0.6 : 1,
          }}
        >
          {pdfLoading ? 'Připravuji PDF…' : 'Stáhnout PDF'}
        </button>
      </div>

      <PlanBadge plan={plan} />

      {platbyInfo && (
        <div role="status" style={{
          margin: '0.75rem 0', padding: '0.8rem 1rem', borderRadius: 8,
          border: '1px solid var(--gold)', color: 'var(--star-white)', fontSize: '0.9rem',
        }}>
          Online platba se právě připravuje. Placenou variantu vám zpřístupníme po domluvě, napište nám prosím přes sekci Konzultace.
        </div>
      )}

      <div ref={reportRef}>

      {/* Hlavička reportu, údaje o společnosti */}
      {wizardState.company?.company_name && (
        <div className="pdf-nobreak" style={{
          background: 'var(--purple-deep)', border: '1px solid var(--purple-mid)', borderRadius: 10,
          padding: '1rem 1.25rem', marginBottom: '1.25rem', pageBreakInside: 'avoid',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem' }}>
            <div>
              <div style={{ fontWeight: 800, fontSize: '1.05rem', color: 'var(--star-white)' }}>
                {wizardState.company.company_name}
              </div>
              <div style={{ fontSize: '0.82rem', color: 'var(--text-dim)', marginTop: '0.15rem' }}>
                IČO: {wizardState.company.ico}
              </div>
            </div>
            <div style={{ fontSize: '0.82rem', color: 'var(--text-dim)', textAlign: 'right' }}>
              <div>{wizardState.company.contact_name}{wizardState.company.contact_position ? ` · ${wizardState.company.contact_position}` : ''}</div>
              <div>{wizardState.company.contact_email}{wizardState.company.contact_phone ? ` · ${wizardState.company.contact_phone}` : ''}</div>
              <div style={{ marginTop: '0.25rem', fontSize: '0.75rem', color: 'var(--text-dim)' }}>
                {new Date().toLocaleDateString('cs-CZ')}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Blok: Očištění zisku (SDE / Adjusted EBITDA) ─────────────────── */}
      {wizardState.profitCleaning && !wizardState.profitCleaning.skipped && wizardState.profitCleaning.adjusted_ebitda && (
        <div className="pdf-nobreak" style={{
          background: 'rgba(107,63,160,0.06)', border: '1px solid rgba(155,89,212,0.25)',
          borderRadius: 10, padding: '1rem 1.25rem', marginBottom: '1.25rem',
          pageBreakInside: 'avoid',
        }}>
          <div style={{ fontWeight: 700, fontSize: '0.9rem', color: 'var(--purple-light)', marginBottom: '0.75rem' }}>
            Očištění zisku, základ pro valuaci
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
            <div style={{ background: 'rgba(34,197,94,0.07)', border: '1px solid rgba(34,197,94,0.2)', borderRadius: 8, padding: '0.75rem' }}>
              <div style={{ fontSize: '0.73rem', color: 'rgba(47,143,91,0.45)', fontWeight: 700, marginBottom: '0.3rem', textTransform: 'uppercase' }}>SDE (kupující = vlastník)</div>
              <div style={{ fontWeight: 800, fontSize: '1.05rem', color: 'var(--star-white)' }}>{fmt(wizardState.profitCleaning.sde)} Kč</div>
              <div style={{ fontSize: '0.78rem', color: 'var(--text-dim)', marginTop: '0.2rem' }}>
                {wizardState.profitCleaning.sde > wizardState.profitCleaning.raw_ebitda
                  ? `+${fmt(wizardState.profitCleaning.sde - wizardState.profitCleaning.raw_ebitda)} Kč oproti účetní EBITDA`
                  : 'stejné jako účetní EBITDA'}
              </div>
            </div>
            <div style={{ background: 'rgba(107,63,160,0.08)', border: '1px solid rgba(155,89,212,0.2)', borderRadius: 8, padding: '0.75rem' }}>
              <div style={{ fontSize: '0.73rem', color: 'var(--purple-light)', fontWeight: 700, marginBottom: '0.3rem', textTransform: 'uppercase' }}>Adjusted EBITDA (investor)</div>
              <div style={{ fontWeight: 800, fontSize: '1.05rem', color: 'var(--star-white)' }}>{fmt(wizardState.profitCleaning.adjusted_ebitda)} Kč</div>
              <div style={{ fontSize: '0.78rem', color: 'var(--text-dim)', marginTop: '0.2rem' }}>
                základ pro tento výpočet valuace
              </div>
            </div>
          </div>
          <div style={{ fontSize: '0.8rem', color: 'var(--text-dim)', lineHeight: 1.55 }}>
            <strong>SDE</strong> (Seller's Discretionary Earnings) = pro kupujícího, který firmu sám povede.
            {' '}<strong>Adjusted EBITDA</strong> = normalizovaná na tržní náhradu manažera, základ pro investora, který najme externního ředitele.
            Valuace je počítána z Adjusted EBITDA.
          </div>
        </div>
      )}

      {/* Hlavní sekce, záporná EBITDA má speciální banner, jinak normální metriky */}
      {isNegativeEbitda ? (
        <NegativeEbitdaBanner
          ebitda={v.ebitda}
          metrics={metrics}
          bq={bq}
          onSwitchToAudit={auditDostupny ? () => requestUpgrade('audit') : null}
          busy={checkoutBusy || upgradeLoading}
          priceLabel={bypassPayment ? '' : ' · 4 990 Kč'}
          alreadyHasAudit={showAudit && !!wizardState.auditResult}
        />
      ) : null}
      {isNegativeEbitda && (
        <>
          {/* Hodnotu při ztrátě dává metoda podle podkladů (majetková,
              tržby, likvidační), ne násobek zisku. */}
          <NavrhZVysledku wizardState={wizardState} />
          {showPro && <div id="sec-zprava"><ZpravaPanel wizardState={wizardState} /></div>}
        </>
      )}
      {!isNegativeEbitda && (
        <>
          <NavrhZVysledku wizardState={wizardState} />
          <FreeSection v={v} segment={seg} metrics={metrics} bq={bq} scenarios={scenarios} problems={problems} plan={plan} onUpgrade={requestUpgrade} auditDostupny={auditDostupny} navrh={result?.navrh} />
          {criticalProblem && (
            <div className="error-message" style={{ marginBottom: '1.25rem' }}>
              Kritické varování: {criticalProblem.name_cs}{criticalProblem.action_cs ? `, ${criticalProblem.action_cs}` : ''}
            </div>
          )}
        </>
      )}

      {/* PRO sekce, pro 'pro' a 'komplet' */}
      {showPro && !isNegativeEbitda && (
        <>
          <div id="sec-zprava"><ZpravaPanel wizardState={wizardState} /></div>
          <ResultsTOC sections={[
            ['sec-health', 'Finanční zdraví'],
            ['sec-kpi', 'KPI vs benchmark'],
            ['sec-bq', 'Quality Score'],
            ['sec-reco', 'Doporučení'],
            ['sec-risks', 'Rizikové faktory'],
            ['sec-growth', 'Jak zvýšit hodnotu'],
            ['sec-whatif', 'What-if simulace'],
            ['sec-investor', 'Pohled investora'],
            ['sec-exit', 'Exit Readiness'],
            ['sec-breakdown', 'Detail výpočtu'],
            ['sec-report', 'Expertní report'],
            ['sec-actions', 'Prioritní akce'],
          ]} />
          <div id="sec-health" className="pdf-nobreak" style={{ pageBreakInside: 'avoid' }}><HealthCards fh={fh} /></div>
          <div id="sec-kpi" className="pdf-nobreak" style={{ pageBreakInside: 'avoid' }}><KPIDashboard metrics={metrics} valuation={v} segment={seg} /></div>
          <div id="sec-bq" className="pdf-nobreak" style={{ pageBreakInside: 'avoid' }}><BQBars bq={bq} /></div>
          <div id="sec-reco" className="pdf-nobreak" style={{ pageBreakInside: 'avoid' }}><Recommendations problems={problems} bq={bq} metrics={{ ...metrics, per_1pct_rrs_value: v.per_1pct_rrs_value }} /></div>
          <div id="sec-risks" className="pdf-nobreak" style={{ pageBreakInside: 'avoid' }}><ProblemsPanel problems={problems} /></div>
          <div id="sec-growth"><ValuationGrowthPlan v={v} metrics={metrics} problems={problems} scenarios={scenarios} /></div>
          <div id="sec-whatif"><WhatIfSimulator wizardState={wizardState} baseValuation={v} /></div>
          <div id="sec-investor"><InvestorView aiReport={aiReport} loading={false} /></div>
          <div id="sec-exit" className="pdf-nobreak" style={{ pageBreakInside: 'avoid' }}><ExitReadinessScore bq={bq} /></div>
          <div id="sec-breakdown" className="pdf-nobreak" style={{ pageBreakInside: 'avoid' }}><ValuationBreakdown v={v} /></div>
          <div id="sec-report"><ValuationAISection
            aiReport={aiReport}
            onGenerate={() => requestUpgrade('pro')}
            loading={false}
            error={null}
            tier="pro"
          /></div>
          <div id="sec-actions"><PriorityActionPlan problems={problems} aiReport={aiReport} v={v} /></div>
        </>
      )}

      {/* Oddělovač mezi PRO a AUDIT v komplet variantě */}
      {plan === 'komplet' && !isNegativeEbitda && (
        <div style={{
          margin: '2rem 0', padding: '1rem 0',
          borderTop: '2px solid var(--purple-mid)', borderBottom: '2px solid var(--purple-mid)',
          textAlign: 'center', pageBreakBefore: 'always',
        }}>
          <span style={{ fontSize: '0.82rem', fontWeight: 700, color: 'var(--gold)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            Konzultantský audit
          </span>
        </div>
      )}

      {/* Most auditu k valuaci je vypnutý (plán v2, O5): násobil roční ušlý
          zisk násobkem a sliboval nárůst hodnoty větší než celá firma.
          Ušlý zisk se ukazuje po oblastech v sekci auditu níže, bez
          přepočtu na hodnotu firmy, dokud audit nebude stát na vzorku
          srovnatelných firem (etapa 5). */}

      {/* AUDIT sekce, pro 'audit' a 'komplet' */}
      {showAudit && (
        <AuditSection
          auditData={wizardState.auditResult}
          onRun={handleRunAudit}
          loading={auditLoading}
          error={auditError}
        />
      )}

      {/* Upgrade tlačítka, ve FREE */}
      {/* Při ztrátě se PRO kupuje kvůli oceňovací zprávě v PDF. */}
      {plan === 'free' && (
        <div style={{
          background: 'rgba(155, 89, 212, 0.06)',
          border: '2px solid var(--purple-bright)',
          borderRadius: 14, padding: '1.5rem', textAlign: 'center', marginBottom: '1.5rem',
        }}>
          <div style={{ fontWeight: 800, fontSize: '1.05rem', color: 'var(--star-white)', marginBottom: '0.4rem' }}>
            Chcete kompletní analýzu s konkrétními doporučeními?
          </div>
          <div style={{ fontSize: '0.85rem', color: 'var(--text-dim)', marginBottom: '1rem' }}>
            Data už máme, stačí kliknout a analýza se vygeneruje.
          </div>
          <div style={{ display: 'flex', gap: '0.65rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button onClick={() => requestUpgrade('pro')} disabled={checkoutBusy} className="btn-primary" style={{ fontSize: '0.85rem' }}>
              PRO valuace · 6 990 Kč
            </button>
            {auditDostupny && <>
            <button onClick={() => requestUpgrade('audit')} disabled={checkoutBusy} className="btn-ghost" style={{ fontSize: '0.85rem' }}>
              Audit · 4 990 Kč
            </button>
            <button onClick={() => requestUpgrade('komplet')} disabled={checkoutBusy} className="btn-primary" style={{ fontSize: '0.85rem', background: 'var(--btn-bg)' }}>
              PRO + Audit · 9 990 Kč (ušetříte 1 990 Kč)
            </button>
            </>}
          </div>
        </div>
      )}

      {/* Dokoupení, PRO → přidat Audit */}
      {plan === 'pro' && !isNegativeEbitda && auditDostupny && (
        <div style={{
          background: 'rgba(234, 88, 12, 0.06)',
          border: '2px solid var(--gold)',
          borderRadius: 14, padding: '1.25rem', textAlign: 'center', marginBottom: '1.5rem',
        }}>
          <div style={{ fontWeight: 700, fontSize: '0.95rem', color: 'var(--star-white)', marginBottom: '0.35rem' }}>
            Doplňte si audit a získejte kompletní obraz
          </div>
          <div style={{ fontSize: '0.82rem', color: 'var(--text-dim)', marginBottom: '0.75rem' }}>
            Máte PRO valuaci. Přidejte audit ztraceného zisku za zvýhodněnou cenu.
          </div>
          <button onClick={() => requestUpgrade('komplet', { chargeTier: 'audit' })} disabled={checkoutBusy} className="btn-primary" style={{ fontSize: '0.85rem', background: 'linear-gradient(135deg, var(--gold), var(--gold))' }}>
            Přidat audit{!bypassPayment && billingEnabled ? ' (s věrnostní slevou)' : ''}
          </button>
        </div>
      )}

      {/* Dokoupení, AUDIT → přidat PRO */}
      {plan === 'audit' && !isNegativeEbitda && (
        <div style={{
          background: 'rgba(155, 89, 212, 0.06)',
          border: '2px solid var(--purple-bright)',
          borderRadius: 14, padding: '1.25rem', textAlign: 'center', marginBottom: '1.5rem',
        }}>
          <div style={{ fontWeight: 700, fontSize: '0.95rem', color: 'var(--star-white)', marginBottom: '0.35rem' }}>
            Chcete i detailní valuaci s pohledem investora?
          </div>
          <div style={{ fontSize: '0.82rem', color: 'var(--text-dim)', marginBottom: '0.75rem' }}>
            Máte audit. Přidejte PRO valuaci za zvýhodněnou cenu.
          </div>
          <button onClick={() => requestUpgrade('komplet', { chargeTier: 'pro' })} disabled={checkoutBusy} className="btn-primary" style={{ fontSize: '0.85rem' }}>
            Přidat PRO valuaci{!bypassPayment && billingEnabled ? ' (s věrnostní slevou)' : ''}
          </button>
        </div>
      )}

      {/* Chat k valuaci, jen k zaplacené variantě (server to hlídá také) */}
      {(showPro || showAudit) && <div id="sec-chat"><ValuaceChat wizardState={wizardState} /></div>}

      {/* Disclaimer */}
      <div style={{ padding: '0.75rem 1rem', background: 'var(--purple-deep)', borderRadius: 8, fontSize: '0.78rem', color: 'var(--text-dim)', borderLeft: '3px solid var(--purple-mid)', marginBottom: '1.5rem' }}>
        Tato valuace je orientační odhad. Skutečná tržní hodnota závisí na kupujícím, výsledcích due diligence a aktuálních tržních podmínkách.
      </div>
      </div>{/* /reportRef */}

      <div className="button-group">
        {onBack && <button onClick={onBack} className="btn-secondary">Zpět na výběr plánu</button>}
        <button onClick={onReset} className="btn-secondary">Nový výpočet</button>
      </div>
    </div>
  );
}
